import React, { useState, useRef, useMemo } from 'react';
import { 
  Wallet, Gem, Home, Car, Briefcase, Plus, Edit2, Trash2, Check, X, 
  ChevronUp, ChevronDown, MoveUp, MoveDown, DollarSign, Building2,
  MapPin, Calendar, Percent, TrendingUp, CreditCard, PiggyBank, ArrowUpDown,
  ChevronLeft, ChevronRight, Upload, Image, XCircle, Maximize2, Minimize2,
  FileText, ExternalLink, Loader2, Eye, EyeOff, Download, Camera, Save, FileSpreadsheet,
  Star, Crosshair, Speaker, Palette
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import * as XLSX from 'xlsx';
import { PropertyMap } from './PropertyMap';

// Import extracted modal components
import { 
  VehicleDetailModal, 
  RealEstateDetailModal, 
  JewelryDetailModal,
  GunDetailModal,
  ElectronicsDetailModal,
  ArtDetailModal,
  PrivateInvestmentDetailModal,
  ASSET_CONFIGS 
} from './modals';

// Panel move buttons
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : 'text-white/60 hover:text-white hover:bg-white/20'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : 'text-white/60 hover:text-white hover:bg-white/20'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);
// Column configurations for each asset type's grid layout
const COLUMN_CONFIGS = {
  CASH: {
    gridCols: 'grid-cols-[minmax(100px,1.5fr)_minmax(80px,1fr)_70px] md:grid-cols-[minmax(140px,1.5fr)_minmax(120px,1fr)_100px_100px_90px_42px]',
    headers: [
      { label: 'Institution', align: 'left' },
      { label: 'Account', align: 'left' },
      { label: 'Type', align: 'left' },
      { label: 'Amount', align: 'right', hideOnMobile: true },
      { label: 'Account #', align: 'left', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  JEWELRY: {
    gridCols: 'grid-cols-[60px_minmax(70px,1fr)_minmax(100px,1.5fr)] md:grid-cols-[minmax(140px,1.2fr)_minmax(150px,1.5fr)_minmax(80px,1fr)_90px_90px_70px_42px]',
    headers: [
      { label: 'Type', align: 'left', sortKey: 'name' },
      { label: 'Brand', align: 'left', sortKey: 'brand' },
      { label: 'Model', align: 'left', sortKey: 'model' },
      { label: 'Cost', align: 'right', sortKey: 'cost', hideOnMobile: true },
      { label: 'Value', align: 'right', sortKey: 'value', hideOnMobile: true },
      { label: 'Change', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  'REAL ESTATE': {
    gridCols: 'grid-cols-[28px_minmax(80px,1fr)_60px_50px] md:grid-cols-[minmax(120px,1.5fr)_minmax(150px,2fr)_minmax(100px,1fr)_60px_90px_90px_70px_42px]',
    headers: [
      { label: 'Property', align: 'left', sortKey: 'name', hideLabelOnMobile: true },
      { label: 'Address', align: 'left', sortKey: 'address' },
      { label: 'Type', align: 'left', sortKey: 'type' },
      { label: 'Year', align: 'center', sortKey: 'purchaseYear' },
      { label: 'Cost', align: 'right', sortKey: 'cost', hideOnMobile: true },
      { label: 'Value', align: 'right', sortKey: 'value', hideOnMobile: true },
      { label: 'Change', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  VEHICLES: {
    gridCols: 'grid-cols-[minmax(120px,1.5fr)_minmax(100px,1fr)_60px] md:grid-cols-[minmax(160px,1.5fr)_150px_60px_minmax(90px,1fr)_minmax(80px,1fr)_minmax(70px,1fr)_60px_75px_75px_42px]',
    headers: [
      { label: 'Vehicle', align: 'left', sortKey: 'year' },
      { label: 'VIN', align: 'left', sortKey: 'vin' },
      { label: 'Plate', align: 'left', sortKey: 'plate' },
      { label: 'Purchased', align: 'left', hideOnMobile: true, sortKey: 'platform' },
      { label: 'Insured By', align: 'left', hideOnMobile: true, sortKey: 'insuredBy' },
      { label: 'Sirius', align: 'left', hideOnMobile: true, sortKey: 'sirius' },
      { label: 'Mileage', align: 'right', hideOnMobile: true, sortKey: 'mileage' },
      { label: 'Cost', align: 'right', hideOnMobile: true, sortKey: 'cost' },
      { label: 'Value', align: 'right', hideOnMobile: true, sortKey: 'value' },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  'PRIVATE INVESTMENTS': {
    gridCols: 'grid-cols-[28px_minmax(65px,1fr)_85px_75px] md:grid-cols-[minmax(140px,2fr)_minmax(100px,1fr)_100px_100px_80px_50px]',
    headers: [
      { label: 'Investment', align: 'left', hideLabelOnMobile: true },
      { label: 'Name', align: 'left', hideOnDesktop: true },
      { label: 'Type', align: 'left' },
      { label: 'Invested', align: 'right', hideOnMobile: true },
      { label: 'Value', align: 'right' },
      { label: 'Return', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  FIREARMS: {
    gridCols: 'grid-cols-[60px_minmax(70px,1fr)_minmax(100px,1.5fr)] md:grid-cols-[minmax(140px,1.2fr)_minmax(130px,1.2fr)_minmax(80px,1fr)_90px_90px_70px_42px]',
    headers: [
      { label: 'Type', align: 'left', sortKey: 'gunType' },
      { label: 'Make/Model', align: 'left', sortKey: 'gunMakeModel' },
      { label: 'Caliber', align: 'left', sortKey: 'gunCaliber' },
      { label: 'Cost', align: 'right', sortKey: 'cost', hideOnMobile: true },
      { label: 'Value', align: 'right', sortKey: 'value', hideOnMobile: true },
      { label: 'Change', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  ELECTRONICS: {
    gridCols: 'grid-cols-[60px_minmax(70px,1fr)_minmax(100px,1.5fr)] md:grid-cols-[minmax(140px,1.2fr)_minmax(130px,1.2fr)_minmax(80px,1fr)_90px_90px_70px_42px]',
    headers: [
      { label: 'Type', align: 'left', sortKey: 'electronicsType' },
      { label: 'Brand/Model', align: 'left', sortKey: 'name' },
      { label: 'Condition', align: 'left', sortKey: 'condition' },
      { label: 'Cost', align: 'right', sortKey: 'cost', hideOnMobile: true },
      { label: 'Value', align: 'right', sortKey: 'value', hideOnMobile: true },
      { label: 'Change', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  },
  ART: {
    gridCols: 'grid-cols-[60px_minmax(70px,1fr)_minmax(100px,1.5fr)] md:grid-cols-[minmax(140px,1.2fr)_minmax(130px,1.2fr)_minmax(80px,1fr)_90px_90px_70px_42px]',
    headers: [
      { label: 'Artwork', align: 'left', sortKey: 'name' },
      { label: 'Artist', align: 'left', sortKey: 'artArtist' },
      { label: 'Medium/Year', align: 'left', sortKey: 'artMedium' },
      { label: 'Cost', align: 'right', sortKey: 'cost', hideOnMobile: true },
      { label: 'Value', align: 'right', sortKey: 'value', hideOnMobile: true },
      { label: 'Change', align: 'right', hideOnMobile: true },
      { label: '', align: 'right', hideOnMobile: true }
    ]
  }
};

// Sort options for each asset type
const SORT_OPTIONS = {
  CASH: [
    { key: 'name', label: 'Name' },
    { key: 'value', label: 'Balance' },
    { key: 'platform', label: 'Bank' }
  ],
  JEWELRY: [
    { key: 'name', label: 'Name' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Cost' }
  ],
  'REAL ESTATE': [
    { key: 'name', label: 'Name' },
    { key: 'value', label: 'Value' },
    { key: 'platform', label: 'Type' }
  ],
  VEHICLES: [
    { key: 'year', label: 'Year' },
    { key: 'make', label: 'Make' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Cost' }
  ],
  'PRIVATE INVESTMENTS': [
    { key: 'name', label: 'Name' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Invested' }
  ],
  FIREARMS: [
    { key: 'name', label: 'Name' },
    { key: 'gunType', label: 'Type' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Cost' }
  ],
  ELECTRONICS: [
    { key: 'name', label: 'Name' },
    { key: 'electronicsType', label: 'Type' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Cost' }
  ],
  ART: [
    { key: 'name', label: 'Name' },
    { key: 'artArtist', label: 'Artist' },
    { key: 'value', label: 'Value' },
    { key: 'cost', label: 'Cost' }
  ]
};

// Individual Asset Tab Content
const AssetTabContent = ({ 
  assetType, 
  assets, 
  formatCurrency, 
  onAdd, 
  onEdit, 
  onDelete,
  isViewer,
  editingId,
  editForm,
  setEditForm,
  onEditSave,
  onEditCancel,
  API_URL,
  userEmail,
  onVehicleDetailClick, // Callback for opening vehicle detail modal
  onRealEstateDetailClick, // Callback for opening real estate detail modal
  onJewelryDetailClick, // Callback for opening jewelry detail modal
  onGunDetailClick, // Callback for opening gun/firearm detail modal
  onElectronicsDetailClick, // Callback for opening electronics detail modal
  onArtDetailClick, // Callback for opening art detail modal
  onPrivateInvestmentDetailClick, // Callback for opening private investment detail modal
  hideValues = false, // Hide dollar values in this panel
  // External sort state for vehicles (lifted to parent for modal sync)
  externalSortBy,
  externalSortDir,
  onSortChange,
  // Jewelry view mode props
  jewelryViewMode,
  onToggleJewelryView,
  jewelryGallerySize,
  onToggleJewelrySize,
  // Vehicle view mode props
  vehicleViewMode,
  onToggleVehicleView,
  vehicleGallerySize,
  onToggleVehicleSize,
  // Real Estate view mode props
  realEstateViewMode,
  onToggleRealEstateView,
  realEstateGallerySize,
  onToggleRealEstateSize,
  // Firearms view mode props
  firearmsViewMode,
  onToggleFirearmsView,
  firearmsGallerySize,
  onToggleFirearmsSize,
  // Electronics view mode props
  electronicsViewMode,
  onToggleElectronicsView,
  electronicsGallerySize,
  onToggleElectronicsSize,
  // Art view mode props
  artViewMode,
  onToggleArtView,
  artGallerySize,
  onToggleArtSize,
  // Jewelry Excel import/export props
  onJewelryExport,
  onJewelryImport
}) => {
  // Use local sort state unless external is provided (for vehicles)
  const [localSortBy, setLocalSortBy] = useState('name');
  const [localSortDir, setLocalSortDir] = useState('asc');
  
  // Vehicle filter state
  const [yearFilter, setYearFilter] = useState('all');
  const [makeFilter, setMakeFilter] = useState('all');
  const [jewelryTypeFilter, setJewelryTypeFilter] = useState('all');
  const [jewelryBrandFilter, setJewelryBrandFilter] = useState('all');
  const [jewelryMaterialFilter, setJewelryMaterialFilter] = useState('all');
  // Firearms filter state
  const [gunTypeFilter, setGunTypeFilter] = useState('all');
  const [gunManufacturerFilter, setGunManufacturerFilter] = useState('all');
  
  // Hide sold/disposed assets toggle - persisted per asset type
  const [hideSoldAssets, setHideSoldAssets] = useState(() => {
    try {
      const saved = localStorage.getItem(`hideSold_${assetType}`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch { return true; }
  });
  
  const toggleHideSold = () => {
    setHideSoldAssets(prev => {
      const next = !prev;
      try { localStorage.setItem(`hideSold_${assetType}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  
  // File input ref for jewelry import
  const jewelryImportRef = useRef(null);

  // Real estate map view toggle - persisted
  const [reMapView, setReMapView] = useState(() => {
    try { return localStorage.getItem('realEstateMapView') === 'true'; } catch { return false; }
  });
  const toggleReMapView = () => {
    setReMapView(prev => {
      try { localStorage.setItem('realEstateMapView', String(!prev)); } catch {}
      return !prev;
    });
  };
  const MapToggleButton = ({ testId, mobile }) => (
    <button
      data-testid={testId}
      onClick={toggleReMapView}
      className={`flex items-center gap-1 text-xs border transition-colors ${mobile ? 'px-3 py-1.5 rounded-full' : 'px-2 py-1 rounded'} ${
        reMapView ? 'bg-blue-600 border-blue-600 text-white' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
      }`}
      title={reMapView ? 'Hide Map' : 'Show Map'}
    >
      <MapPin className="w-3.5 h-3.5" />
      <span>{reMapView ? 'Hide Map' : 'Map'}</span>
    </button>
  );
  
  // Helper to conditionally hide currency values - uses hideValues prop
  // Note: formatCurrency from parent may have global privacyMode built in,
  // but the local hideValues toggle should be the sole source of truth for this panel
  const displayCurrency = (amount) => {
    if (hideValues) return '•••••';
    // Use raw formatting to bypass any global privacy mode
    return parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  const sortBy = externalSortBy !== undefined ? externalSortBy : localSortBy;
  const sortDir = externalSortDir !== undefined ? externalSortDir : localSortDir;
  
  const config = ASSET_CONFIGS[assetType];
  const columnConfig = COLUMN_CONFIGS[assetType];
  const sortOptions = SORT_OPTIONS[assetType] || [];
  const Icon = config.icon;
  const allAssetsOfType = assets.filter(a => a.investmentType === assetType);
  
  // Get unique years and makes for filter dropdowns (for vehicles)
  const uniqueYears = assetType === 'VEHICLES' 
    ? [...new Set(allAssetsOfType.map(a => a.vehicleYear).filter(Boolean))].sort((a, b) => b - a)
    : [];
  const uniqueMakes = assetType === 'VEHICLES'
    ? [...new Set(allAssetsOfType.map(a => a.vehicleMake).filter(Boolean))].sort()
    : [];
  
  // Get unique types, brands, and materials for filter dropdowns (for jewelry)
  const uniqueJewelryTypes = assetType === 'JEWELRY'
    ? [...new Set(allAssetsOfType.map(a => a.jewelryType).filter(Boolean))].sort()
    : [];
  const uniqueJewelryBrands = assetType === 'JEWELRY'
    ? [...new Set(allAssetsOfType.map(a => a.brand).filter(Boolean))].sort()
    : [];
  const uniqueJewelryMaterials = assetType === 'JEWELRY'
    ? [...new Set(allAssetsOfType.map(a => a.material).filter(Boolean))].sort()
    : [];
  
  // Apply filters for vehicles and jewelry
  const filteredAssets = assetType === 'VEHICLES'
    ? allAssetsOfType.filter(a => {
        if (yearFilter !== 'all' && a.vehicleYear !== parseInt(yearFilter)) return false;
        if (makeFilter !== 'all' && a.vehicleMake !== makeFilter) return false;
        return true;
      })
    : assetType === 'JEWELRY'
    ? allAssetsOfType.filter(a => {
        if (jewelryTypeFilter !== 'all' && a.jewelryType !== jewelryTypeFilter) return false;
        if (jewelryBrandFilter !== 'all' && a.brand !== jewelryBrandFilter) return false;
        if (jewelryMaterialFilter !== 'all' && a.material !== jewelryMaterialFilter) return false;
        return true;
      })
    : allAssetsOfType;
  
  // Sort assets
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'year':
        aVal = a.vehicleYear || 0;
        bVal = b.vehicleYear || 0;
        break;
      case 'make':
        aVal = (a.vehicleMake || '').toLowerCase();
        bVal = (b.vehicleMake || '').toLowerCase();
        break;
      case 'value':
        aVal = a.currentPrice || a.shares || 0;
        bVal = b.currentPrice || b.shares || 0;
        break;
      case 'cost':
        aVal = a.costBasis || 0;
        bVal = b.costBasis || 0;
        break;
      case 'platform':
        aVal = (a.platform || '').toLowerCase();
        bVal = (b.platform || '').toLowerCase();
        break;
      case 'vin':
        aVal = (a.vin || '').toLowerCase();
        bVal = (b.vin || '').toLowerCase();
        break;
      case 'plate':
        aVal = (a.licensePlate || '').toLowerCase();
        bVal = (b.licensePlate || '').toLowerCase();
        break;
      case 'insuredBy':
        aVal = (a.insuredBy || '').toLowerCase();
        bVal = (b.insuredBy || '').toLowerCase();
        break;
      case 'sirius':
        aVal = (a.sirius || '').toLowerCase();
        bVal = (b.sirius || '').toLowerCase();
        break;
      case 'mileage':
        aVal = a.mileage || 0;
        bVal = b.mileage || 0;
        break;
      case 'address':
        aVal = (a.name || a.propertyAddress || '').toLowerCase();
        bVal = (b.name || b.propertyAddress || '').toLowerCase();
        break;
      case 'type':
        aVal = (a.platform || '').toLowerCase();
        bVal = (b.platform || '').toLowerCase();
        break;
      case 'gunType':
        aVal = (a.gunType || '').toLowerCase();
        bVal = (b.gunType || '').toLowerCase();
        break;
      case 'gunCaliber':
        aVal = (a.gunCaliber || '').toLowerCase();
        bVal = (b.gunCaliber || '').toLowerCase();
        break;
      case 'gunMakeModel':
        aVal = (`${a.gunManufacturer || ''} ${a.gunModel || ''}`.trim() || a.symbol || '').toLowerCase();
        bVal = (`${b.gunManufacturer || ''} ${b.gunModel || ''}`.trim() || b.symbol || '').toLowerCase();
        break;
      case 'electronicsType':
        aVal = (a.electronicsType || '').toLowerCase();
        bVal = (b.electronicsType || '').toLowerCase();
        break;
      case 'condition':
        aVal = (a.condition || '').toLowerCase();
        bVal = (b.condition || '').toLowerCase();
        break;
      case 'artArtist':
        aVal = (a.artArtist || a.artist || '').toLowerCase();
        bVal = (b.artArtist || b.artist || '').toLowerCase();
        break;
      case 'artMedium':
        aVal = (a.artMedium || '').toLowerCase();
        bVal = (b.artMedium || '').toLowerCase();
        break;
      case 'brand':
        aVal = (a.brand || a.electronicsType || '').toLowerCase();
        bVal = (b.brand || b.electronicsType || '').toLowerCase();
        break;
      case 'model':
        aVal = (a.model || a.electronicsModel || '').toLowerCase();
        bVal = (b.model || b.electronicsModel || '').toLowerCase();
        break;
      case 'name':
        aVal = (a.symbol || a.name || '').toLowerCase();
        bVal = (b.symbol || b.name || '').toLowerCase();
        break;
      case 'purchaseYear':
        aVal = a.purchaseYear || 0;
        bVal = b.purchaseYear || 0;
        break;
      default:
        aVal = (a.name || a.symbol || '').toLowerCase();
        bVal = (b.name || b.symbol || '').toLowerCase();
    }
    
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });
  
  const handleSort = (field) => {
    const newDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    const newSortBy = field;
    
    // Use external callback if provided, otherwise update local state
    if (onSortChange) {
      onSortChange(newSortBy, newDir);
    } else {
      setLocalSortBy(newSortBy);
      setLocalSortDir(newDir);
    }
  };
  
  // Count disposed items for toggle visibility
  const disposedCount = allAssetsOfType.filter(a => a.disposed).length;
  
  // Apply hide-sold filter for display
  const displayAssets = hideSoldAssets ? sortedAssets.filter(a => !a.disposed) : sortedAssets;
  
  // Calculate total value - handle legacy data where value might be in shares
  // Exclude disposed/sold items from totals
  const activeAssets = filteredAssets.filter(a => !a.disposed);
  const totalValue = activeAssets.reduce((sum, a) => {
    // Legacy data check: if shares > 1, it's the actual value; otherwise use currentPrice
    if (a.shares && a.shares > 1) {
      return sum + a.shares;
    }
    return sum + (a.currentPrice || 0);
  }, 0);

  const totalCost = activeAssets.reduce((sum, a) => {
    // For jewelry, real estate, vehicles, private investments - costBasis is purchase/investment price
    // For cash - costBasis might be interest rate, so skip it
    if (assetType === 'CASH') return sum;
    return sum + (a.costBasis || 0);
  }, 0);

  const gainLoss = assetType !== 'CASH' ? totalValue - totalCost : 0;
  const gainLossPercent = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
  
  // For vehicles/assets, show as "Depreciated" if negative, "Appreciated" if positive
  const depreciationLabel = assetType === 'VEHICLES' ? (gainLoss < 0 ? 'Depreciated' : 'Appreciated') : (gainLoss < 0 ? 'Loss' : 'Gain');

  return (
    <div className="space-y-3">
      {/* Compact Summary Row - hidden on mobile */}
      <div className="hidden md:flex items-center justify-between flex-wrap gap-2 text-sm">
        <div className="flex items-center gap-4">
          <span className="text-slate-500">
            {displayAssets.length} {
              assetType === 'CASH' 
                ? (filteredAssets.length === 1 ? 'cash account' : 'cash accounts')
                : assetType === 'VEHICLES'
                ? (filteredAssets.length === 1 ? 'vehicle' : 'vehicles')
                : assetType === 'REAL ESTATE'
                ? (filteredAssets.length === 1 ? 'property' : 'properties')
                : assetType === 'PRIVATE INVESTMENTS'
                ? (filteredAssets.length === 1 ? 'private investment' : 'private investments')
                : assetType === 'JEWELRY'
                ? (filteredAssets.length === 1 ? 'jewelry item' : 'jewelry items')
                : assetType === 'FIREARMS'
                ? (filteredAssets.length === 1 ? 'firearm' : 'firearms')
                : config.label.toLowerCase()
            }
            {assetType === 'VEHICLES' && (yearFilter !== 'all' || makeFilter !== 'all') && allAssetsOfType.length !== displayAssets.length && (
              <span className="text-slate-400"> of {allAssetsOfType.filter(a => !a.disposed).length} total</span>
            )}
            {assetType === 'JEWELRY' && (jewelryTypeFilter !== 'all' || jewelryBrandFilter !== 'all' || jewelryMaterialFilter !== 'all') && allAssetsOfType.length !== filteredAssets.length && (
              <span className="text-slate-400"> of {allAssetsOfType.length} total</span>
            )}
            {assetType === 'FIREARMS' && (gunTypeFilter !== 'all' || gunManufacturerFilter !== 'all') && allAssetsOfType.length !== filteredAssets.length && (
              <span className="text-slate-400"> of {allAssetsOfType.length} total</span>
            )}
          </span>
          {assetType !== 'CASH' && (
            <>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">Cost: <span className="font-medium text-slate-700">{displayCurrency(totalCost)}</span></span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">Current Value: <span className="font-bold text-slate-900">{displayCurrency(totalValue)}</span></span>
              <span className="text-slate-400">•</span>
              <span className={gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {depreciationLabel}: {hideValues ? '•••••' : `${gainLoss >= 0 ? '+' : ''}${displayCurrency(gainLoss)}`}
              </span>
            </>
          )}
          {assetType === 'CASH' && (
            <>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">Total: <span className="font-bold text-slate-900">{displayCurrency(totalValue)}</span></span>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Filter dropdowns for Vehicles */}
          {assetType === 'VEHICLES' && filteredAssets.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {/* View Toggle Button */}
              {onToggleVehicleView && (
                <button
                  onClick={onToggleVehicleView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    vehicleViewMode === 'gallery' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={vehicleViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {vehicleViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Button - only show in gallery mode */}
              {vehicleViewMode === 'gallery' && onToggleVehicleSize && (
                <div className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 rounded-md border border-slate-200">
                  <button
                    onClick={() => onToggleVehicleSize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      vehicleGallerySize === 'large'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleVehicleSize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      vehicleGallerySize === 'compact'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleVehicleSize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      vehicleGallerySize === 'tiny'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
              <span className="text-xs text-slate-400">Filter:</span>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="all">All Years</option>
                {uniqueYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select
                value={makeFilter}
                onChange={(e) => setMakeFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="all">All Makes</option>
                {uniqueMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
              {(yearFilter !== 'all' || makeFilter !== 'all') && (
                <button
                  onClick={() => { setYearFilter('all'); setMakeFilter('all'); }}
                  className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-500 hover:bg-slate-200"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          
          {/* Filter dropdowns and Gallery toggle for Real Estate */}
          {assetType === 'REAL ESTATE' && allAssetsOfType.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              <MapToggleButton testId="realestate-map-toggle" />
              {/* View Toggle Button */}
              {onToggleRealEstateView && (
                <button
                  onClick={onToggleRealEstateView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    realEstateViewMode === 'gallery' 
                      ? 'bg-blue-100 border-blue-300 text-blue-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={realEstateViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {realEstateViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Button - only show in gallery mode */}
              {realEstateViewMode === 'gallery' && onToggleRealEstateSize && (
                <div className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 rounded-md border border-slate-200">
                  <button
                    onClick={() => onToggleRealEstateSize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      realEstateGallerySize === 'large'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleRealEstateSize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      realEstateGallerySize === 'compact'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleRealEstateSize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      realEstateGallerySize === 'tiny'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Filter dropdowns and Gallery toggle for Firearms */}
          {assetType === 'FIREARMS' && allAssetsOfType.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {/* View Toggle Button */}
              {onToggleFirearmsView && (
                <button
                  onClick={onToggleFirearmsView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    firearmsViewMode === 'gallery' 
                      ? 'bg-slate-200 border-slate-400 text-slate-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={firearmsViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {firearmsViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Button - only show in gallery mode */}
              {firearmsViewMode === 'gallery' && onToggleFirearmsSize && (
                <div className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 rounded-md border border-slate-200">
                  <button
                    onClick={() => onToggleFirearmsSize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      firearmsGallerySize === 'large'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleFirearmsSize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      firearmsGallerySize === 'compact'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleFirearmsSize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      firearmsGallerySize === 'tiny'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
              {/* Type Filter */}
              <select
                value={gunTypeFilter}
                onChange={(e) => setGunTypeFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
              >
                <option value="all">All Types</option>
                <option value="Handgun">Handgun</option>
                <option value="Rifle">Rifle</option>
                <option value="Shotgun">Shotgun</option>
                <option value="Other">Other</option>
              </select>
              {(gunTypeFilter !== 'all' || gunManufacturerFilter !== 'all') && (
                <button
                  onClick={() => { setGunTypeFilter('all'); setGunManufacturerFilter('all'); }}
                  className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-500 hover:bg-slate-200"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          
          {/* Filter dropdowns and Gallery toggle for Electronics */}
          {assetType === 'ELECTRONICS' && allAssetsOfType.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {/* View Toggle Button */}
              {onToggleElectronicsView && (
                <button
                  onClick={onToggleElectronicsView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    electronicsViewMode === 'gallery' 
                      ? 'bg-cyan-100 border-cyan-300 text-cyan-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={electronicsViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {electronicsViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Button - only show in gallery mode */}
              {electronicsViewMode === 'gallery' && onToggleElectronicsSize && (
                <div className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 rounded-md border border-slate-200">
                  <button
                    onClick={() => onToggleElectronicsSize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      electronicsGallerySize === 'large'
                        ? 'bg-white text-cyan-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleElectronicsSize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      electronicsGallerySize === 'compact'
                        ? 'bg-white text-cyan-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleElectronicsSize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      electronicsGallerySize === 'tiny'
                        ? 'bg-white text-cyan-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Gallery toggle for Art */}
          {assetType === 'ART' && allAssetsOfType.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {/* View Toggle Button */}
              {onToggleArtView && (
                <button
                  onClick={onToggleArtView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    artViewMode === 'gallery' 
                      ? 'bg-rose-100 border-rose-300 text-rose-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={artViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {artViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Button - only show in gallery mode */}
              {artViewMode === 'gallery' && onToggleArtSize && (
                <div className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-100 rounded-md border border-slate-200">
                  <button
                    onClick={() => onToggleArtSize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      artGallerySize === 'large'
                        ? 'bg-white text-rose-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleArtSize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      artGallerySize === 'compact'
                        ? 'bg-white text-rose-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleArtSize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      artGallerySize === 'tiny'
                        ? 'bg-white text-rose-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Filter dropdowns for Jewelry */}
          {assetType === 'JEWELRY' && allAssetsOfType.length > 0 && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {/* View Toggle Button */}
              {onToggleJewelryView && (
                <button
                  onClick={onToggleJewelryView}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                    jewelryViewMode === 'gallery' 
                      ? 'bg-purple-100 border-purple-300 text-purple-700' 
                      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={jewelryViewMode === 'list' ? 'Switch to Gallery View' : 'Switch to List View'}
                >
                  {jewelryViewMode === 'list' ? (
                    <>
                      <Image className="w-3.5 h-3.5" />
                      <span>Gallery</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      <span>List</span>
                    </>
                  )}
                </button>
              )}
              {/* Size Toggle Buttons - only show in gallery mode */}
              {jewelryViewMode === 'gallery' && onToggleJewelrySize && (
                <div className="flex items-center gap-0.5 bg-slate-100 rounded p-0.5">
                  <button
                    onClick={() => onToggleJewelrySize('large')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      jewelryGallerySize === 'large'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Large view"
                  >
                    Large
                  </button>
                  <button
                    onClick={() => onToggleJewelrySize('compact')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      jewelryGallerySize === 'compact'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Compact view"
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onToggleJewelrySize('tiny')}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      jewelryGallerySize === 'tiny'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Tiny view - shows more items"
                  >
                    Tiny
                  </button>
                </div>
              )}
              <span className="text-xs text-slate-400">Filter:</span>
              <select
                value={jewelryTypeFilter}
                onChange={(e) => setJewelryTypeFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                <option value="all">All Types</option>
                {uniqueJewelryTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={jewelryBrandFilter}
                onChange={(e) => setJewelryBrandFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                <option value="all">All Brands</option>
                {uniqueJewelryBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              <select
                value={jewelryMaterialFilter}
                onChange={(e) => setJewelryMaterialFilter(e.target.value)}
                className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                <option value="all">All Materials</option>
                {uniqueJewelryMaterials.map(material => (
                  <option key={material} value={material}>{material}</option>
                ))}
              </select>
              {(jewelryTypeFilter !== 'all' || jewelryBrandFilter !== 'all' || jewelryMaterialFilter !== 'all') && (
                <button
                  onClick={() => { setJewelryTypeFilter('all'); setJewelryBrandFilter('all'); setJewelryMaterialFilter('all'); }}
                  className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-500 hover:bg-slate-200"
                >
                  Clear
                </button>
              )}
              {/* Import/Export buttons for Jewelry */}
              <div className="flex items-center gap-1 ml-2 border-l border-slate-200 pl-2">
                <button
                  onClick={() => onJewelryExport && onJewelryExport(allAssetsOfType)}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 transition-colors"
                  title="Export to Excel"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button
                  onClick={() => jewelryImportRef.current?.click()}
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 transition-colors"
                  title="Import from Excel"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>Import</span>
                </button>
                <input
                  ref={jewelryImportRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    if (e.target.files?.[0] && onJewelryImport) {
                      onJewelryImport(e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  className="hidden"
                />
              </div>
            </div>
          )}
          
          {/* Sort buttons for non-vehicle asset types */}
          {assetType !== 'VEHICLES' && assetType !== 'JEWELRY' && sortOptions.length > 0 && filteredAssets.length > 1 && (
            <div className="flex items-center gap-1 mr-2">
              <span className="text-xs text-slate-400">Sort:</span>
              {sortOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => handleSort(opt.key)}
                  className={`px-2 py-1 text-xs rounded ${sortBy === opt.key ? 'bg-slate-200 text-slate-700 font-medium' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  {opt.label} {sortBy === opt.key && (sortDir === 'asc' ? '↑' : '↓')}
                </button>
              ))}
            </div>
          )}
          
          {/* Hide Sold Toggle - only show if there are disposed items */}
          {disposedCount > 0 && (
            <button
              onClick={toggleHideSold}
              data-testid="hide-sold-toggle"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-full border transition-colors ${
                hideSoldAssets
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200'
              }`}
              title={hideSoldAssets ? `Show ${disposedCount} sold items` : `Hide ${disposedCount} sold items`}
            >
              {hideSoldAssets ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              <span>{hideSoldAssets ? `Show Sold (${disposedCount})` : `Hide Sold (${disposedCount})`}</span>
            </button>
          )}
          
          {/* Add Button */}
          {!isViewer && (
            <Button 
              onClick={() => onAdd(assetType)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" /> Add {config.label}
            </Button>
          )}
        </div>
      </div>
      
      {/* Mobile-only controls for Vehicles */}
      {assetType === 'VEHICLES' && sortedAssets.length > 0 && onToggleVehicleView && (
        <div className="md:hidden flex items-center justify-between mb-2 px-1">
          <span className="text-xs text-slate-500">{displayAssets.length} items</span>
          <div className="flex items-center gap-2">
            {/* Size Toggle - only show in gallery mode */}
            {vehicleViewMode === 'gallery' && onToggleVehicleSize && (
              <button
                onClick={onToggleVehicleSize}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${
                  vehicleGallerySize === 'compact'
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                    : 'bg-slate-100 border-slate-200 text-slate-600'
                }`}
              >
                {vehicleGallerySize === 'large' ? (
                  <Minimize2 className="w-3.5 h-3.5" />
                ) : (
                  <Maximize2 className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              onClick={onToggleVehicleView}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                vehicleViewMode === 'gallery' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                  : 'bg-slate-100 border-slate-200 text-slate-600'
              }`}
            >
              {vehicleViewMode === 'list' ? (
                <>
                  <Image className="w-3.5 h-3.5" />
                  <span>Gallery</span>
                </>
              ) : (
                <>
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>List</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile-only controls for Real Estate */}
      {assetType === 'REAL ESTATE' && sortedAssets.length > 0 && onToggleRealEstateView && (
        <div className="md:hidden flex items-center justify-between mb-2 px-1">
          <span className="text-xs text-slate-500">{displayAssets.length} items</span>
          <div className="flex items-center gap-2">
            <MapToggleButton testId="realestate-map-toggle-mobile" mobile />
            {disposedCount > 0 && (
              <button
                onClick={toggleHideSold}
                data-testid="hide-sold-toggle-mobile"
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full border transition-colors ${
                  hideSoldAssets ? 'bg-red-50 border-red-300 text-red-700' : 'bg-slate-100 border-slate-200 text-slate-500'
                }`}
              >
                {hideSoldAssets ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                <span>{hideSoldAssets ? `Show Sold (${disposedCount})` : `Hide Sold (${disposedCount})`}</span>
              </button>
            )}
            {/* Size Toggle - only show in gallery mode */}
            {realEstateViewMode === 'gallery' && onToggleRealEstateSize && (
              <button
                onClick={onToggleRealEstateSize}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${
                  realEstateGallerySize === 'compact'
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-slate-100 border-slate-200 text-slate-600'
                }`}
              >
                {realEstateGallerySize === 'large' ? (
                  <Minimize2 className="w-3.5 h-3.5" />
                ) : (
                  <Maximize2 className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              onClick={onToggleRealEstateView}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                realEstateViewMode === 'gallery' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700' 
                  : 'bg-slate-100 border-slate-200 text-slate-600'
              }`}
            >
              {realEstateViewMode === 'list' ? (
                <>
                  <Image className="w-3.5 h-3.5" />
                  <span>Gallery</span>
                </>
              ) : (
                <>
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>List</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile-only controls for Jewelry */}
      {assetType === 'JEWELRY' && sortedAssets.length > 0 && onToggleJewelryView && (
        <div className="md:hidden mb-2 px-1">
          {/* Brand filter row */}
          <div className="flex items-center gap-2 mb-2">
            <select
              value={jewelryBrandFilter}
              onChange={(e) => setJewelryBrandFilter(e.target.value)}
              className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option value="all">All Brands</option>
              {uniqueJewelryBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            {jewelryBrandFilter !== 'all' && (
              <button
                onClick={() => setJewelryBrandFilter('all')}
                className="px-2 py-1.5 text-xs rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200"
              >
                Clear
              </button>
            )}
          </div>
          {/* Items count and view toggle row */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{displayAssets.length} items</span>
            <button
              onClick={onToggleJewelryView}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                jewelryViewMode === 'gallery' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700' 
                  : 'bg-slate-100 border-slate-200 text-slate-600'
              }`}
            >
              {jewelryViewMode === 'list' ? (
                <>
                  <Image className="w-3.5 h-3.5" />
                  <span>Gallery</span>
                </>
              ) : (
                <>
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>List</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile-only controls for Electronics */}
      {assetType === 'ELECTRONICS' && sortedAssets.length > 0 && onToggleElectronicsView && (
        <div className="md:hidden flex items-center justify-between mb-2 px-1">
          <span className="text-xs text-slate-500">{displayAssets.length} items</span>
          <div className="flex items-center gap-2">
            {/* Size Toggle - only show in gallery mode */}
            {electronicsViewMode === 'gallery' && onToggleElectronicsSize && (
              <button
                onClick={onToggleElectronicsSize}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${
                  electronicsGallerySize === 'compact'
                    ? 'bg-cyan-100 border-cyan-300 text-cyan-700'
                    : 'bg-slate-100 border-slate-200 text-slate-600'
                }`}
              >
                {electronicsGallerySize === 'large' ? (
                  <Minimize2 className="w-3.5 h-3.5" />
                ) : (
                  <Maximize2 className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              onClick={onToggleElectronicsView}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                electronicsViewMode === 'gallery' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700' 
                  : 'bg-slate-100 border-slate-200 text-slate-600'
              }`}
            >
              {electronicsViewMode === 'list' ? (
                <>
                  <Image className="w-3.5 h-3.5" />
                  <span>Gallery</span>
                </>
              ) : (
                <>
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>List</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Assets List */}
      {displayAssets.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No {config.label.toLowerCase()} assets yet</p>
          {!isViewer && (
            <p className="text-sm mt-1">Click "Add {config.label}" to get started</p>
          )}
        </div>
      ) : assetType === 'REAL ESTATE' && reMapView ? (
        <PropertyMap
          assets={displayAssets}
          API_URL={API_URL}
          hideValues={hideValues}
          displayCurrency={displayCurrency}
          onOpenDetails={onRealEstateDetailClick}
        />
      ) : assetType === 'VEHICLES' && vehicleViewMode === 'gallery' ? (
        /* Vehicle Gallery View */
        <div 
          data-testid="vehicle-gallery-view" 
          className={`grid gap-4 p-2 ${
            vehicleGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2'
              : vehicleGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            // Vehicle photos are stored as objects {url, name, ...} so extract the URL
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            const vehicleName = `${asset.vehicleYear || ''} ${asset.vehicleMake || ''} ${asset.vehicleModel || ''}`.trim() || asset.symbol || 'Vehicle';
            return (
              <div 
                key={asset.id}
                data-testid={`vehicle-gallery-card-${asset.id}`}
                onClick={() => onVehicleDetailClick && onVehicleDetailClick(asset.id)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-emerald-300'
                } ${vehicleGallerySize === 'tiny' ? 'rounded-lg' : ''}`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Vehicle Name */}
                  <div className={`absolute top-0 left-0 right-0 bg-emerald-700/75 backdrop-blur-sm text-white z-10 truncate ${
                    vehicleGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : vehicleGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {vehicleName}
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={vehicleName} 
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Hide broken image and show fallback
                        e.target.style.display = 'none';
                        e.target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon - shown when no photo or photo fails to load */}
                  <div className={`w-full h-full flex items-center justify-center ${firstPhotoUrl ? 'hidden' : ''}`}>
                    <Car className={vehicleGallerySize === 'tiny' ? 'w-4 h-4 text-emerald-200' : vehicleGallerySize === 'compact' ? 'w-6 h-6 text-emerald-200' : 'w-12 h-12 text-emerald-200'} />
                  </div>
                  {/* Completion star indicator */}
                  <div className={`absolute top-1 right-1 ${
                    vehicleGallerySize === 'tiny' ? 'w-4 h-4' : vehicleGallerySize === 'compact' ? 'w-5 h-5' : 'w-6 h-6'
                  } bg-white/80 rounded-full flex items-center justify-center shadow-sm`}>
                    {asset.isComplete ? (
                      <Star className={`${vehicleGallerySize === 'tiny' ? 'w-3 h-3' : vehicleGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-500 fill-amber-500`} />
                    ) : (
                      <Star className={`${vehicleGallerySize === 'tiny' ? 'w-3 h-3' : vehicleGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-300`} strokeWidth={2} />
                    )}
                  </div>
                  {/* Sold/Disposed badge */}
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    vehicleGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : vehicleGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {vehicleGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {asset.vehicleMake || 'Unknown Make'}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {asset.vehicleYear} {asset.vehicleModel || ''}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : assetType === 'REAL ESTATE' && realEstateViewMode === 'gallery' ? (
        /* Real Estate Gallery View */
        <div 
          data-testid="realestate-gallery-view" 
          className={`grid gap-4 p-2 ${
            realEstateGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2'
              : realEstateGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            // Handle both string URLs and object {url, name, ...} formats
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            return (
              <div 
                key={asset.id}
                data-testid={`realestate-gallery-card-${asset.id}`}
                onClick={() => onRealEstateDetailClick && onRealEstateDetailClick(asset.id)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-blue-300'
                } ${realEstateGallerySize === 'tiny' ? 'rounded-lg' : ''}`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Address */}
                  <div
                    data-testid={`realestate-gallery-banner-${asset.id}`}
                    title="Open in Maps"
                    onClick={(e) => {
                      e.stopPropagation();
                      const addr = (asset.name && asset.name !== asset.symbol ? asset.name : asset.propertyAddress) || asset.symbol || '';
                      const isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document;
                      const url = isApple ? `https://maps.apple.com/?q=${encodeURIComponent(addr)}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
                      window.open(url, '_blank', 'noopener');
                    }}
                    className={`absolute top-0 left-0 right-0 bg-blue-700/75 backdrop-blur-sm text-white z-10 flex items-center gap-1 cursor-pointer hover:bg-blue-800/90 transition-colors ${
                    realEstateGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : realEstateGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    <MapPin className="w-[1em] h-[1em] shrink-0" />
                    <span className="truncate hover:underline">{(asset.name && asset.name !== asset.symbol ? asset.name : asset.propertyAddress) || asset.symbol || 'Property'}</span>
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={asset.symbol || asset.name || 'Property'} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Hide broken image and show fallback
                        e.target.style.display = 'none';
                        e.target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon - shown when no photo or photo fails to load */}
                  <div className={`w-full h-full flex items-center justify-center ${firstPhotoUrl ? 'hidden' : ''}`}>
                    <Building2 className={realEstateGallerySize === 'tiny' ? 'w-4 h-4 text-blue-200' : realEstateGallerySize === 'compact' ? 'w-6 h-6 text-blue-200' : 'w-12 h-12 text-blue-200'} />
                  </div>
                  {/* Sold/Disposed badge */}
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    realEstateGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : realEstateGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {realEstateGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {asset.symbol || asset.name || 'Property'}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {asset.propertyType || asset.platform || 'Real Estate'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : assetType === 'FIREARMS' && firearmsViewMode === 'gallery' ? (
        /* Firearms Gallery View */
        <div 
          data-testid="firearms-gallery-view" 
          className={`grid gap-4 p-2 ${
            firearmsGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2'
              : firearmsGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            // Handle both string URLs and object {url, name, ...} formats
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            const gunName = asset.symbol || `${asset.gunManufacturer || ''} ${asset.gunModel || ''}`.trim() || 'Firearm';
            return (
              <div 
                key={asset.id}
                data-testid={`firearms-gallery-card-${asset.id}`}
                onClick={() => onGunDetailClick && onGunDetailClick(asset.id, sortedAssets)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-slate-400'
                } ${
                  firearmsGallerySize === 'tiny' ? 'rounded-lg' : ''
                }`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Manufacturer & Model */}
                  <div className={`absolute top-0 left-0 right-0 bg-slate-800/75 backdrop-blur-sm text-white z-10 truncate ${
                    firearmsGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : firearmsGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {(asset.gunManufacturer || asset.gunModel) 
                      ? `${asset.gunManufacturer || ''}${asset.gunManufacturer && asset.gunModel ? ' ' : ''}${asset.gunModel || ''}`
                      : gunName}
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={gunName} 
                      className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon - shown when no photo or photo fails to load */}
                  <div className={`w-full h-full flex items-center justify-center ${firstPhotoUrl ? 'hidden' : ''}`}>
                    <Crosshair className={firearmsGallerySize === 'tiny' ? 'w-4 h-4 text-slate-300' : firearmsGallerySize === 'compact' ? 'w-6 h-6 text-slate-300' : 'w-12 h-12 text-slate-300'} />
                  </div>
                  {/* Completion star indicator */}
                  <div className={`absolute top-1 right-1 ${
                    firearmsGallerySize === 'tiny' ? 'w-4 h-4' : firearmsGallerySize === 'compact' ? 'w-5 h-5' : 'w-6 h-6'
                  } bg-white/80 rounded-full flex items-center justify-center shadow-sm`}>
                    {asset.isComplete ? (
                      <Star className={`${firearmsGallerySize === 'tiny' ? 'w-3 h-3' : firearmsGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-500 fill-amber-500`} />
                    ) : (
                      <Star className={`${firearmsGallerySize === 'tiny' ? 'w-3 h-3' : firearmsGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-300`} strokeWidth={2} />
                    )}
                  </div>
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    firearmsGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : firearmsGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {firearmsGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {gunName}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {asset.gunType || ''} {asset.gunCaliber ? `• ${asset.gunCaliber}` : ''}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : assetType === 'JEWELRY' && jewelryViewMode === 'gallery' ? (
        /* Jewelry Gallery View */
        <div 
          data-testid="jewelry-gallery-view" 
          className={`grid gap-4 p-2 ${
            jewelryGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2'
              : jewelryGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            // Handle both string URLs and object {url, name, ...} formats
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            return (
              <div 
                key={asset.id}
                data-testid={`jewelry-gallery-card-${asset.id}`}
                onClick={() => onJewelryDetailClick && onJewelryDetailClick(asset.id, sortedAssets)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-purple-300'
                } ${
                  jewelryGallerySize === 'tiny' ? 'rounded-lg' : ''
                }`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Jewelry Name */}
                  <div data-testid={`jewelry-gallery-banner-${asset.id}`} className={`absolute top-0 left-0 right-0 bg-purple-700/75 backdrop-blur-sm text-white z-10 truncate ${
                    jewelryGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : jewelryGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {`${asset.brand || ''} ${asset.model || ''}`.trim() || asset.symbol || 'Jewelry'}
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={asset.brand || asset.symbol || 'Jewelry'} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Gem className={jewelryGallerySize === 'tiny' ? 'w-4 h-4 text-purple-200' : jewelryGallerySize === 'compact' ? 'w-6 h-6 text-purple-200' : 'w-12 h-12 text-purple-200'} />
                    </div>
                  )}
                  {/* Completion star indicator */}
                  <div className={`absolute top-1 right-1 ${
                    jewelryGallerySize === 'tiny' ? 'w-4 h-4' : jewelryGallerySize === 'compact' ? 'w-5 h-5' : 'w-6 h-6'
                  } bg-white/80 rounded-full flex items-center justify-center shadow-sm`}>
                    {asset.isComplete ? (
                      <Star className={`${jewelryGallerySize === 'tiny' ? 'w-3 h-3' : jewelryGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-500 fill-amber-500`} />
                    ) : (
                      <Star className={`${jewelryGallerySize === 'tiny' ? 'w-3 h-3' : jewelryGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-300`} strokeWidth={2} />
                    )}
                  </div>
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    jewelryGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : jewelryGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {jewelryGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {asset.brand || 'Unknown Brand'}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {asset.model || asset.symbol || 'No Model'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : assetType === 'ELECTRONICS' && electronicsViewMode === 'gallery' ? (
        /* Electronics Gallery View */
        <div 
          data-testid="electronics-gallery-view" 
          className={`grid gap-4 p-2 ${
            electronicsGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2'
              : electronicsGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            // Handle both string URLs and object {url, name, ...} formats
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            const electronicsName = asset.symbol || `${asset.electronicsBrand || ''} ${asset.electronicsModel || ''}`.trim() || 'Electronics';
            return (
              <div 
                key={asset.id}
                data-testid={`electronics-gallery-card-${asset.id}`}
                onClick={() => onElectronicsDetailClick && onElectronicsDetailClick(asset.id, sortedAssets)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-cyan-300'
                } ${
                  electronicsGallerySize === 'tiny' ? 'rounded-lg' : ''
                }`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Brand & Model */}
                  <div data-testid={`electronics-gallery-banner-${asset.id}`} className={`absolute top-0 left-0 right-0 bg-cyan-700/75 backdrop-blur-sm text-white z-10 truncate ${
                    electronicsGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : electronicsGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {`${asset.electronicsBrand || ''} ${asset.electronicsModel || ''}`.trim() || electronicsName}
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={electronicsName} 
                      className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon - shown when no photo or photo fails to load */}
                  <div className={`w-full h-full flex items-center justify-center ${firstPhotoUrl ? 'hidden' : ''}`}>
                    <Speaker className={electronicsGallerySize === 'tiny' ? 'w-4 h-4 text-cyan-300' : electronicsGallerySize === 'compact' ? 'w-6 h-6 text-cyan-300' : 'w-12 h-12 text-cyan-300'} />
                  </div>
                  {/* Completion star indicator */}
                  <div className={`absolute top-1 right-1 ${
                    electronicsGallerySize === 'tiny' ? 'w-4 h-4' : electronicsGallerySize === 'compact' ? 'w-5 h-5' : 'w-6 h-6'
                  } bg-white/80 rounded-full flex items-center justify-center shadow-sm`}>
                    {asset.isComplete ? (
                      <Star className={`${electronicsGallerySize === 'tiny' ? 'w-3 h-3' : electronicsGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-500 fill-amber-500`} />
                    ) : (
                      <Star className={`${electronicsGallerySize === 'tiny' ? 'w-3 h-3' : electronicsGallerySize === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-300`} strokeWidth={2} />
                    )}
                  </div>
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    electronicsGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : electronicsGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {electronicsGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {asset.electronicsBrand || 'Unknown Brand'}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {asset.electronicsModel || asset.symbol || 'No Model'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : assetType === 'ART' && artViewMode === 'gallery' ? (
        /* Art Gallery View */
        <div 
          data-testid="art-gallery-view" 
          className={`grid gap-4 p-2 ${
            artGallerySize === 'tiny'
              ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2'
              : artGallerySize === 'compact' 
                ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          }`}
        >
          {displayAssets.map((asset) => {
            const firstPhotoData = asset.photos && asset.photos.length > 0 ? asset.photos[0] : null;
            const firstPhotoUrl = firstPhotoData ? (typeof firstPhotoData === 'string' ? firstPhotoData : firstPhotoData.url) : null;
            const artName = asset.symbol || asset.name || 'Artwork';
            return (
              <div 
                key={asset.id}
                data-testid={`art-gallery-card-${asset.id}`}
                onClick={() => onArtDetailClick && onArtDetailClick(asset.id, sortedAssets)}
                className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group ${
                  asset.disposed ? 'border-red-200 opacity-60' : 'border-slate-200 hover:border-rose-300'
                } ${
                  artGallerySize === 'tiny' ? 'rounded-lg' : ''
                }`}
              >
                {/* Photo Area */}
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {/* Top banner - Title & Artist */}
                  <div data-testid={`art-gallery-banner-${asset.id}`} className={`absolute top-0 left-0 right-0 bg-rose-700/75 backdrop-blur-sm text-white z-10 truncate ${
                    artGallerySize === 'tiny' ? 'text-[7px] px-1 py-0.5' : artGallerySize === 'compact' ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {asset.artArtist ? `${artName} — ${asset.artArtist}` : artName}
                  </div>
                  {firstPhotoUrl ? (
                    <img 
                      src={firstPhotoUrl} 
                      alt={artName} 
                      className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon */}
                  <div className={`w-full h-full flex items-center justify-center ${firstPhotoUrl ? 'hidden' : ''}`}>
                    <Palette className={artGallerySize === 'tiny' ? 'w-4 h-4 text-rose-300' : artGallerySize === 'compact' ? 'w-6 h-6 text-rose-300' : 'w-12 h-12 text-rose-300'} />
                  </div>
                  {asset.disposed && (
                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm uppercase">Sold</div>
                  )}
                  {/* Value badge */}
                  <div className={`absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white rounded-full font-medium ${
                    artGallerySize === 'tiny' ? 'text-[8px] px-1 py-0.5' : artGallerySize === 'compact' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}>
                    {displayCurrency(asset.currentPrice || asset.shares || 0)}
                  </div>
                </div>
                {/* Info Area - hide in compact and tiny mode */}
                {artGallerySize === 'large' && (
                  <div className="p-3 bg-white">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {artName}
                    </div>
                    {asset.artArtist && (
                      <div className="text-xs text-slate-500 truncate">
                        by {asset.artArtist}
                      </div>
                    )}
                    {asset.artMedium && (
                      <div className="text-xs text-slate-400 mt-1 truncate">
                        {asset.artMedium}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1">
          {/* Column headers for all asset types */}
          {columnConfig && (
            <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 px-3 py-1 text-xs text-slate-400 font-medium uppercase tracking-wide`}>
              {columnConfig.headers.map((header, idx) => (
                <div 
                  key={idx} 
                  className={`${header.align === 'right' ? 'text-right' : ''} ${header.hideOnMobile ? 'hidden md:block' : ''} ${header.hideOnDesktop ? 'md:hidden' : ''} ${header.sortKey ? 'cursor-pointer hover:text-slate-600 select-none' : ''}`}
                  onClick={header.sortKey ? () => handleSort(header.sortKey) : undefined}
                >
                  {header.hideLabelOnMobile ? (
                    <span className="hidden md:inline">{header.label}</span>
                  ) : (
                    header.label
                  )}
                  {header.sortKey && sortBy === header.sortKey && (
                    <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {displayAssets.map((asset, index) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              config={config}
              assetType={assetType}
              columnConfig={columnConfig}
              formatCurrency={formatCurrency}
              isViewer={isViewer}
              isEditing={editingId === asset.id}
              editForm={editForm}
              setEditForm={setEditForm}
              onEdit={() => onEdit(asset)}
              onDelete={() => onDelete(asset.id)}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
              onVehicleClick={assetType === 'VEHICLES' && onVehicleDetailClick ? () => onVehicleDetailClick(asset.id) : null}
              onRealEstateClick={assetType === 'REAL ESTATE' && onRealEstateDetailClick ? () => onRealEstateDetailClick(asset.id) : null}
              onJewelryClick={assetType === 'JEWELRY' && onJewelryDetailClick ? () => onJewelryDetailClick(asset.id, sortedAssets) : null}
              onGunClick={assetType === 'FIREARMS' && onGunDetailClick ? () => onGunDetailClick(asset.id, sortedAssets) : null}
              onElectronicsClick={assetType === 'ELECTRONICS' && onElectronicsDetailClick ? () => onElectronicsDetailClick(asset.id, sortedAssets) : null}
              onArtClick={assetType === 'ART' && onArtDetailClick ? () => onArtDetailClick(asset.id, sortedAssets) : null}
              onPrivateInvestmentClick={assetType === 'PRIVATE INVESTMENTS' && onPrivateInvestmentDetailClick ? () => onPrivateInvestmentDetailClick(asset.id, sortedAssets) : null}
              hideValues={hideValues}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual Asset Card
const AssetCard = ({ 
  asset, 
  config, 
  assetType,
  columnConfig,
  formatCurrency, 
  isViewer,
  isEditing,
  editForm,
  setEditForm,
  onEdit,
  onDelete,
  onEditSave,
  onEditCancel,
  onVehicleClick,
  onRealEstateClick,
  onJewelryClick,
  onGunClick,
  onElectronicsClick,
  onArtClick,
  onPrivateInvestmentClick,
  hideValues = false
}) => {
  const Icon = config.icon;
  
  // Helper to conditionally hide currency values
  // Use raw formatting to bypass any global privacy mode - local toggle is source of truth
  const displayCurrency = (amount) => {
    if (hideValues) return '•••••';
    return parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Determine if this card should be clickable
  const onClick = onVehicleClick || onRealEstateClick || onJewelryClick || onGunClick || onElectronicsClick || onArtClick || onPrivateInvestmentClick;
  
  // For non-market assets, the value is stored in currentPrice (shares=1)
  // Legacy data might have value in shares, so check both
  let value;
  if (asset.shares && asset.shares > 1) {
    // Legacy data: value was stored in shares
    value = asset.shares;
  } else {
    // Standard: value is in currentPrice
    value = asset.currentPrice || 0;
  }
  
  const cost = asset.costBasis || 0;
  const gainLoss = assetType !== 'CASH' ? value - cost : 0;
  const gainLossPercent = cost > 0 ? ((value - cost) / cost) * 100 : 0;

  if (isEditing) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {config.fields.map((field) => (
            <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              <label className="text-xs text-slate-500 mb-1 block">{field.label}</label>
              {field.type === 'select' ? (
                <select
                  value={editForm[field.key] || ''}
                  onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                >
                  <option value="">Select...</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={editForm[field.key] || ''}
                  onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                  placeholder={field.label}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white min-h-[80px] resize-y"
                  rows={3}
                />
              ) : (
                <Input
                  type={field.type}
                  value={editForm[field.key] || ''}
                  onChange={(e) => setEditForm({ ...editForm, [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
                  placeholder={field.label}
                  className="text-sm"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onEditCancel}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={onEditSave} className="bg-emerald-500 hover:bg-emerald-600">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </div>
    );
  }

  // Render grid-based layout for all asset types
  const renderGridLayout = () => {
    switch (assetType) {
      case 'CASH':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Institution - mobile: text only, desktop: Icon + Institution */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="md:hidden text-sm text-slate-600 truncate">{asset.platform || '—'}</span>
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <div className="p-1 rounded bg-emerald-100 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="font-medium text-slate-700 text-sm truncate">
                  {asset.platform || '—'}
                </span>
              </div>
            </div>
            
            {/* Account Name - mobile and desktop */}
            <div className="truncate">
              <span className="text-sm text-slate-600">{asset.symbol || asset.name || '—'}</span>
            </div>
            
            {/* Account Type */}
            <div className="truncate">
              {asset.accountType ? (
                <span className="text-sm text-slate-600">{asset.accountType}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Amount/Balance - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Account Number - hidden on mobile */}
            <div className="truncate hidden md:block">
              {asset.accountNumber ? (
                <span className="text-sm text-slate-500 font-mono">{asset.accountNumber}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'JEWELRY':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Type - mobile: jewelryType, desktop: Icon + Item Name */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="md:hidden text-sm text-slate-600 truncate">{asset.jewelryType || '—'}</span>
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <div className="p-1 rounded bg-purple-100 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-purple-600" />
                </div>
                <span className="font-medium text-slate-700 text-sm truncate">
                  {asset.symbol || asset.name}
                </span>
              </div>
            </div>
            
            {/* Brand - mobile: brand, desktop: model */}
            <div className="truncate">
              <span className="md:hidden text-sm text-slate-600">{asset.brand || '—'}</span>
              <span className="hidden md:inline text-sm text-slate-600">{asset.model || '—'}</span>
            </div>
            
            {/* Model - mobile: model, desktop: location */}
            <div className="truncate">
              <span className="md:hidden text-sm text-slate-600">{asset.model || '—'}</span>
              <span className="hidden md:inline text-sm text-slate-500">{asset.platform || '—'}</span>
            </div>
            
            {/* Cost - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Change - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onJewelryClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'REAL ESTATE':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Icon only on mobile, Icon + Property Name on desktop */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1 rounded bg-blue-100 flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <span className="hidden md:inline font-medium text-slate-700 text-sm truncate">
                {asset.symbol || asset.name}
              </span>
            </div>
            
            {/* Address - Zillow or LoopNet Link based on property type */}
            <div className="truncate">
              {(() => {
                const address = asset.name && asset.name !== asset.symbol ? asset.name : asset.propertyAddress;
                const propertyType = (asset.platform || '').toLowerCase();
                const isCommercialOrLand = propertyType === 'commercial' || propertyType === 'land';
                // LoopNet doesn't support direct address search, so use Google to search LoopNet
                const baseUrl = isCommercialOrLand 
                  ? `https://www.google.com/search?q=site:loopnet.com+${encodeURIComponent(address)}`
                  : `https://www.zillow.com/homes/${encodeURIComponent(address)}_rb/`;
                
                if (address) {
                  return (
                    <a 
                      href={baseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      title={isCommercialOrLand ? 'Search on LoopNet (via Google)' : 'View on Zillow'}
                    >
                      {address}
                    </a>
                  );
                }
                return <span className="text-sm text-slate-300">—</span>;
              })()}
            </div>
            
            {/* Property Type */}
            <div className="truncate">
              <span className="text-sm text-slate-600">{asset.platform || '—'}</span>
            </div>
            
            {/* Year Acquired */}
            <div className="text-center">
              <span className="text-sm text-slate-600">{asset.purchaseYear || '—'}</span>
            </div>
            
            {/* Cost - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Change - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'VEHICLES':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Icon + Name */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1 rounded bg-orange-100 flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-orange-600" />
              </div>
              <span className="font-medium text-slate-700 text-sm truncate">
                {config.getDisplayName ? config.getDisplayName(asset) : (asset.symbol || asset.name)}
              </span>
            </div>
            
            {/* VIN */}
            <div className="truncate">
              {asset.vin ? (
                <span className="text-sm text-slate-500 font-mono">{asset.vin}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* License Plate */}
            <div>
              {asset.licensePlate ? (
                <span className="text-sm text-slate-600 font-medium">{asset.licensePlate}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Where Purchased - hidden on mobile */}
            <div className="truncate hidden md:block">
              {asset.platform ? (
                <span className="text-sm text-slate-600">{asset.platform}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Insured By - hidden on mobile */}
            <div className="truncate hidden md:block">
              {asset.insuredBy ? (
                <span className="text-sm text-slate-600">{asset.insuredBy}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Sirius - hidden on mobile */}
            <div className="truncate hidden md:block">
              {asset.sirius ? (
                <span className="text-sm text-slate-600">{asset.sirius}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Mileage - hidden on mobile */}
            <div className="text-right hidden md:block">
              {asset.mileage ? (
                <span className="text-sm text-slate-600">{Number(asset.mileage).toLocaleString()}</span>
              ) : (
                <span className="text-sm text-slate-300">—</span>
              )}
            </div>
            
            {/* Cost - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onVehicleClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'FIREARMS':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Type - mobile: gunType, desktop: Icon + Item Name */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="md:hidden text-sm text-slate-600 truncate">{asset.gunType || '—'}</span>
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <div className="p-1 rounded bg-slate-200 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <span className="font-medium text-slate-700 text-sm truncate">
                  {asset.symbol || asset.name || `${asset.gunManufacturer || ''} ${asset.gunModel || ''}`.trim() || 'Firearm'}
                </span>
              </div>
            </div>
            
            {/* Make/Model - mobile: manufacturer, desktop: model */}
            <div className="truncate">
              <span className="md:hidden text-sm text-slate-600">{asset.gunManufacturer || '—'}</span>
              <span className="hidden md:inline text-sm text-slate-600">
                {asset.gunManufacturer && asset.gunModel 
                  ? `${asset.gunManufacturer} ${asset.gunModel}`
                  : asset.gunManufacturer || asset.gunModel || '—'}
              </span>
            </div>
            
            {/* Caliber */}
            <div className="truncate">
              <span className="text-sm text-slate-500">{asset.gunCaliber || '—'}</span>
            </div>
            
            {/* Cost - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Change - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onGunClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'ELECTRONICS':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Type - mobile: electronicsType, desktop: Icon + Item Name */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="md:hidden text-sm text-slate-600 truncate">{asset.electronicsType || '—'}</span>
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <div className="p-1 rounded bg-cyan-100 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-cyan-600" />
                </div>
                <span className="font-medium text-slate-700 text-sm truncate">
                  {asset.symbol || asset.name || `${asset.electronicsBrand || ''} ${asset.electronicsModel || ''}`.trim() || 'Electronics'}
                </span>
              </div>
            </div>
            
            {/* Brand/Model - mobile: brand, desktop: brand + model */}
            <div className="truncate">
              <span className="md:hidden text-sm text-slate-600">{asset.electronicsBrand || '—'}</span>
              <span className="hidden md:inline text-sm text-slate-600">
                {asset.electronicsBrand && asset.electronicsModel 
                  ? `${asset.electronicsBrand} ${asset.electronicsModel}`
                  : asset.electronicsBrand || asset.electronicsModel || '—'}
              </span>
            </div>
            
            {/* Condition */}
            <div className="truncate">
              <span className="text-sm text-slate-500">{asset.condition || '—'}</span>
            </div>
            
            {/* Cost - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Change - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onElectronicsClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'ART':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Type - mobile: medium, desktop: Icon + Artwork Name */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="md:hidden text-sm text-slate-600 truncate">{asset.artMedium || '—'}</span>
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <div className="p-1 rounded bg-rose-100 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-rose-600" />
                </div>
                <span className="font-medium text-slate-700 text-sm truncate">
                  {asset.symbol || asset.name || 'Artwork'}
                </span>
              </div>
            </div>
            
            {/* Artist - mobile: artist, desktop: artist */}
            <div className="truncate">
              <span className="text-sm text-slate-600">{asset.artArtist || '—'}</span>
            </div>
            
            {/* Medium/Year */}
            <div className="truncate">
              <span className="md:hidden text-sm text-slate-500">{asset.artYear || '—'}</span>
              <span className="hidden md:inline text-sm text-slate-500">
                {asset.artMedium ? `${asset.artMedium}${asset.artYear ? ` (${asset.artYear})` : ''}` : asset.artYear || '—'}
              </span>
            </div>
            
            {/* Cost - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Value - desktop only */}
            <div className="text-right hidden md:block">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Change - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onArtClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      case 'PRIVATE INVESTMENTS':
        return (
          <div className={`grid ${columnConfig.gridCols} items-center gap-x-3 text-sm`}>
            {/* Icon only on mobile, Icon + Name on desktop */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1 rounded bg-indigo-100 flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-indigo-600" />
              </div>
              <span className="hidden md:inline font-medium text-slate-700 text-sm truncate">
                {asset.symbol || asset.name}
              </span>
            </div>
            
            {/* Investment Name - visible on mobile only */}
            <div className="truncate md:hidden">
              <span className="font-medium text-slate-700 text-sm">{asset.symbol || asset.name}</span>
            </div>
            
            {/* Investment Type */}
            <div className="truncate">
              <span className="text-sm text-slate-600">{asset.platform || '—'}</span>
            </div>
            
            {/* Invested Amount - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className="text-sm text-slate-500">{displayCurrency(cost)}</span>
            </div>
            
            {/* Current Value */}
            <div className="text-right">
              <span className="text-sm font-semibold text-slate-800">{displayCurrency(value)}</span>
            </div>
            
            {/* Return - hidden on mobile */}
            <div className="text-right hidden md:block">
              <span className={`text-sm ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {gainLoss >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Actions - hidden on mobile */}
            <div className="hidden md:flex items-center justify-end gap-0.5">
              {!isViewer && (
                <>
                  <button onClick={onPrivateInvestmentClick || onEdit} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div 
      className={`border rounded-lg px-3 py-1.5 shadow-sm transition-shadow ${
        asset.disposed 
          ? 'bg-slate-100 border-red-200 asset-sold' 
          : 'bg-white border-slate-200 hover:shadow-md'
      } ${onClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
      onClick={onClick ? (e) => {
        // Don't trigger if clicking on edit/delete buttons
        if (e.target.closest('button')) return;
        onClick();
      } : undefined}
    >
      {renderGridLayout()}
    </div>
  );
};

// Add Asset Modal/Form
const AddAssetForm = ({ assetType, onSave, onCancel }) => {
  const config = ASSET_CONFIGS[assetType];
  const [form, setForm] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      investmentType: assetType,
      currentPrice: form.shares || 0, // Set currentPrice same as value for non-market assets
    });
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Plus className="w-4 h-4" /> Add New {config.label}
      </h4>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {config.fields.map((field) => (
            <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              <label className="text-xs text-slate-500 mb-1 block">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === 'select' ? (
                <select
                  value={form[field.key] || ''}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                  required={field.required}
                >
                  <option value="">Select...</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={form[field.key] || ''}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                  placeholder={field.label}
                  required={field.required}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white min-h-[80px] resize-y"
                  rows={3}
                />
              ) : (
                <Input
                  type={field.type}
                  value={form[field.key] || ''}
                  onChange={(e) => setForm({ ...form, [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
                  placeholder={field.label}
                  required={field.required}
                  className="text-sm"
                  step={field.type === 'number' ? '0.01' : undefined}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-emerald-500 hover:bg-emerald-600">
            <Plus className="w-4 h-4 mr-1" /> Add {config.label}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main Panel Component
const NonMarketAssetsPanel = ({
  stocks,
  formatCurrencyPrivate,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
  isViewer = false,
  onAddStock,
  onEditStock,
  onDeleteStock,
  mobilePreview,
  API_URL,
  userEmail,
  isFullscreen = false,
  onToggleFullscreen,
  onRefresh, // Callback to refresh data without full page reload
  privacyMode = false // Hide sensitive info like item counts
}) => {
  // Load panel expanded state from localStorage
  const getInitialExpandedState = () => {
    try {
      const saved = localStorage.getItem('nonMarketPanelExpanded');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading panel state:', e);
    }
    return true; // Default to expanded
  };
  
  const [isExpanded, setIsExpanded] = useState(getInitialExpandedState);
  
  // Local toggle for hiding values in Non-Market Assets panel - persisted to localStorage
  const [hideNonMarketValues, setHideNonMarketValues] = useState(() => {
    try {
      const saved = localStorage.getItem('hideNonMarketValues');
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  
  // Save hideNonMarketValues to localStorage when it changes
  const toggleHideNonMarketValues = () => {
    setHideNonMarketValues(prev => {
      const newState = !prev;
      try {
        localStorage.setItem('hideNonMarketValues', JSON.stringify(newState));
      } catch (e) {
        console.error('Error saving hide values state:', e);
      }
      return newState;
    });
  };
  
  // Persist expanded state to localStorage
  const handleToggleExpanded = () => {
    setIsExpanded(prev => {
      const newState = !prev;
      try {
        localStorage.setItem('nonMarketPanelExpanded', JSON.stringify(newState));
      } catch (e) {
        console.error('Error saving panel state:', e);
      }
      return newState;
    });
  };
  
  const [activeTab, setActiveTab] = useState(null); // Start with no tab selected
  const [addingType, setAddingType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [vehicleDetailModalId, setVehicleDetailModalId] = useState(null); // Lifted up from AssetTabContent
  const [realEstateDetailModalId, setRealEstateDetailModalId] = useState(null); // For Real Estate detail modal
  const [jewelryDetailModalId, setJewelryDetailModalId] = useState(null); // For Jewelry detail modal
  const [filteredJewelryForModal, setFilteredJewelryForModal] = useState([]); // Store filtered list when modal opens
  const [isAddingNewJewelry, setIsAddingNewJewelry] = useState(false); // For adding new jewelry via modal
  const [gunDetailModalId, setGunDetailModalId] = useState(null); // For Firearms detail modal
  const [filteredGunsForModal, setFilteredGunsForModal] = useState([]); // Store filtered list when modal opens
  const [isAddingNewGun, setIsAddingNewGun] = useState(false); // For adding new firearm via modal
  const [electronicsDetailModalId, setElectronicsDetailModalId] = useState(null); // For Electronics detail modal
  const [filteredElectronicsForModal, setFilteredElectronicsForModal] = useState([]); // Store filtered list when modal opens
  const [isAddingNewElectronics, setIsAddingNewElectronics] = useState(false); // For adding new electronics via modal
  const [artDetailModalId, setArtDetailModalId] = useState(null); // For Art detail modal
  const [filteredArtForModal, setFilteredArtForModal] = useState([]); // Store filtered list when modal opens
  const [isAddingNewArt, setIsAddingNewArt] = useState(false); // For adding new art via modal
  const [privateInvestmentDetailModalId, setPrivateInvestmentDetailModalId] = useState(null); // For Private Investment detail modal
  const [filteredPrivateInvestmentsForModal, setFilteredPrivateInvestmentsForModal] = useState([]); // Store filtered list when modal opens
  const [isAddingNewPrivateInvestment, setIsAddingNewPrivateInvestment] = useState(false); // For adding new private investment via modal
  const [isAddingNewVehicle, setIsAddingNewVehicle] = useState(false); // For adding new vehicle via modal
  const [isAddingNewRealEstate, setIsAddingNewRealEstate] = useState(false); // For adding new real estate via modal
  const [vehicleSortBy, setVehicleSortBy] = useState('name');
  const [vehicleSortDir, setVehicleSortDir] = useState('asc');
  
  // Jewelry filter states
  const [jewelryTypeFilter, setJewelryTypeFilter] = useState('');
  const [jewelryBrandFilter, setJewelryBrandFilter] = useState('');
  const [jewelryViewMode, setJewelryViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('jewelryViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Gallery size states (large = current, compact = half size)
  const [jewelryGallerySize, setJewelryGallerySize] = useState(() => {
    try {
      return localStorage.getItem('jewelryGallerySize') || 'large';
    } catch (e) { return 'large'; }
  });
  const [vehicleGallerySize, setVehicleGallerySize] = useState(() => {
    try {
      return localStorage.getItem('vehicleGallerySize') || 'compact';
    } catch (e) { return 'compact'; }
  });
  const [realEstateGallerySize, setRealEstateGallerySize] = useState(() => {
    try {
      return localStorage.getItem('realEstateGallerySize') || 'compact';
    } catch (e) { return 'compact'; }
  });
  
  // Vehicle view mode state
  const [vehicleViewMode, setVehicleViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('vehicleViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Real Estate view mode state
  const [realEstateViewMode, setRealEstateViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('realEstateViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Firearms view mode state
  const [firearmsViewMode, setFirearmsViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('firearmsViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Firearms gallery size state
  const [firearmsGallerySize, setFirearmsGallerySize] = useState(() => {
    try {
      return localStorage.getItem('firearmsGallerySize') || 'large';
    } catch (e) { return 'large'; }
  });
  
  // Electronics view mode state
  const [electronicsViewMode, setElectronicsViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('electronicsViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Electronics gallery size state
  const [electronicsGallerySize, setElectronicsGallerySize] = useState(() => {
    try {
      return localStorage.getItem('electronicsGallerySize') || 'large';
    } catch (e) { return 'large'; }
  });
  
  // Art view mode state
  const [artViewMode, setArtViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('artViewMode');
      return saved || 'list';
    } catch (e) {
      return 'list';
    }
  });
  
  // Art gallery size state
  const [artGallerySize, setArtGallerySize] = useState(() => {
    try {
      return localStorage.getItem('artGallerySize') || 'large';
    } catch (e) { return 'large'; }
  });
  
  // Toggle jewelry view mode
  const toggleJewelryViewMode = () => {
    const newMode = jewelryViewMode === 'list' ? 'gallery' : 'list';
    setJewelryViewMode(newMode);
    try {
      localStorage.setItem('jewelryViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle vehicle view mode
  const toggleVehicleViewMode = () => {
    const newMode = vehicleViewMode === 'list' ? 'gallery' : 'list';
    setVehicleViewMode(newMode);
    try {
      localStorage.setItem('vehicleViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle real estate view mode
  const toggleRealEstateViewMode = () => {
    const newMode = realEstateViewMode === 'list' ? 'gallery' : 'list';
    setRealEstateViewMode(newMode);
    try {
      localStorage.setItem('realEstateViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle firearms view mode
  const toggleFirearmsViewMode = () => {
    const newMode = firearmsViewMode === 'list' ? 'gallery' : 'list';
    setFirearmsViewMode(newMode);
    try {
      localStorage.setItem('firearmsViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle gallery size functions
  const toggleJewelryGallerySize = (size) => {
    // If size is provided, use it directly, otherwise cycle through sizes
    const newSize = size || (jewelryGallerySize === 'large' ? 'compact' : jewelryGallerySize === 'compact' ? 'tiny' : 'large');
    setJewelryGallerySize(newSize);
    try { localStorage.setItem('jewelryGallerySize', newSize); } catch (e) {}
  };
  const toggleVehicleGallerySize = (size) => {
    // If size is provided, use it directly, otherwise cycle through sizes
    const newSize = size || (vehicleGallerySize === 'large' ? 'compact' : vehicleGallerySize === 'compact' ? 'tiny' : 'large');
    setVehicleGallerySize(newSize);
    try { localStorage.setItem('vehicleGallerySize', newSize); } catch (e) {}
  };
  const toggleRealEstateGallerySize = (size) => {
    // If size is provided, use it directly, otherwise cycle through sizes
    const newSize = size || (realEstateGallerySize === 'large' ? 'compact' : realEstateGallerySize === 'compact' ? 'tiny' : 'large');
    setRealEstateGallerySize(newSize);
    try { localStorage.setItem('realEstateGallerySize', newSize); } catch (e) {}
  };
  const toggleFirearmsGallerySize = (size) => {
    // If size is provided, use it directly, otherwise cycle through sizes
    const newSize = size || (firearmsGallerySize === 'large' ? 'compact' : firearmsGallerySize === 'compact' ? 'tiny' : 'large');
    setFirearmsGallerySize(newSize);
    try { localStorage.setItem('firearmsGallerySize', newSize); } catch (e) {}
  };
  
  // Toggle electronics view mode
  const toggleElectronicsViewMode = () => {
    const newMode = electronicsViewMode === 'list' ? 'gallery' : 'list';
    setElectronicsViewMode(newMode);
    try {
      localStorage.setItem('electronicsViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle electronics gallery size
  const toggleElectronicsGallerySize = (size) => {
    const newSize = size || (electronicsGallerySize === 'large' ? 'compact' : electronicsGallerySize === 'compact' ? 'tiny' : 'large');
    setElectronicsGallerySize(newSize);
    try { localStorage.setItem('electronicsGallerySize', newSize); } catch (e) {}
  };
  
  // Toggle art view mode
  const toggleArtViewMode = () => {
    const newMode = artViewMode === 'list' ? 'gallery' : 'list';
    setArtViewMode(newMode);
    try {
      localStorage.setItem('artViewMode', newMode);
    } catch (e) {}
  };
  
  // Toggle art gallery size
  const toggleArtGallerySize = (size) => {
    const newSize = size || (artGallerySize === 'large' ? 'compact' : artGallerySize === 'compact' ? 'tiny' : 'large');
    setArtGallerySize(newSize);
    try { localStorage.setItem('artGallerySize', newSize); } catch (e) {}
  };

  // Jewelry Excel Export Handler
  const handleJewelryExport = (jewelryItems) => {
    if (!jewelryItems || jewelryItems.length === 0) {
      alert('No jewelry items to export');
      return;
    }
    
    // Define columns for export
    const exportData = jewelryItems.map(item => ({
      'Item Name': item.symbol || '',
      'Type': item.jewelryType || '',
      'Brand': item.brand || '',
      'Model': item.model || '',
      'Material': item.material || '',
      'Serial Number': item.serialNumber || '',
      'Condition': item.condition || '',
      'Original Packaging': item.originalPackaging || '',
      'Storage Location': item.platform || '',
      'Insured By': item.insuredBy || '',
      'Purchase Price': item.costBasis || 0,
      'Appraised Value': item.currentPrice || item.costBasis || 0,
      'Purchase Date': item.purchaseDate || '',
      'Where Purchased': item.purchaseLocation || '',
      'Purchase Condition': item.purchaseCondition || '',
      'Notes': item.notes || ''
    }));
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Item Name
      { wch: 12 }, // Type
      { wch: 15 }, // Brand
      { wch: 25 }, // Model
      { wch: 10 }, // Material
      { wch: 15 }, // Serial Number
      { wch: 12 }, // Condition
      { wch: 18 }, // Original Packaging
      { wch: 18 }, // Storage Location
      { wch: 15 }, // Insured By
      { wch: 15 }, // Purchase Price
      { wch: 15 }, // Appraised Value
      { wch: 12 }, // Purchase Date
      { wch: 20 }, // Where Purchased
      { wch: 15 }, // Purchase Condition
      { wch: 30 }  // Notes
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Jewelry');
    
    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const filename = `jewelry_export_${date}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, filename);
  };

  // Jewelry Excel Import Handler
  const handleJewelryImport = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            alert('No data found in the Excel file');
            return;
          }
          
          // Show confirmation
          const confirmed = window.confirm(
            `Found ${jsonData.length} jewelry item(s) in the file.\n\nDo you want to import them?`
          );
          
          if (!confirmed) return;
          
          let successCount = 0;
          let errorCount = 0;
          let skippedCount = 0;
          let errorMessages = [];
          
          // Get existing jewelry items to check for duplicates
          const existingJewelry = stocks.filter(s => s.investmentType === 'JEWELRY');
          
          // Helper to check if item already exists
          const isDuplicate = (newItem) => {
            const newSerial = toStr(newItem['Serial Number'] || '').toLowerCase().trim();
            const newName = toStr(newItem['Item Name'] || newItem['Name'] || '').toLowerCase().trim();
            const newBrand = toStr(newItem['Brand'] || '').toLowerCase().trim();
            const newModel = toStr(newItem['Model'] || '').toLowerCase().trim();
            
            for (const existing of existingJewelry) {
              // Check by serial number if both have one
              const existingSerial = (existing.serialNumber || '').toLowerCase().trim();
              if (newSerial && existingSerial && newSerial === existingSerial) {
                return { isDupe: true, reason: `Serial Number "${newSerial}" already exists` };
              }
              
              // Check by name + brand + model combination
              const existingName = (existing.symbol || '').toLowerCase().trim();
              const existingBrand = (existing.brand || '').toLowerCase().trim();
              const existingModel = (existing.model || '').toLowerCase().trim();
              
              if (newName && newBrand && newModel &&
                  newName === existingName && 
                  newBrand === existingBrand && 
                  newModel === existingModel) {
                return { isDupe: true, reason: `"${newItem['Item Name']}" already exists` };
              }
            }
            return { isDupe: false };
          };
          
          // Helper to convert values to string
          const toStr = (val) => {
            if (val === null || val === undefined) return '';
            if (typeof val === 'number') {
              if (val > 40000 && val < 50000) {
                const date = new Date((val - 25569) * 86400 * 1000);
                return date.toLocaleDateString();
              }
              return String(val);
            }
            return String(val);
          };
          
          // Helper function to make API call using XMLHttpRequest (avoids fetch response body issues)
          const addJewelryItem = (jewelryData) => {
            return new Promise((resolve) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`);
              xhr.setRequestHeader('Content-Type', 'application/json');
              xhr.onload = function() {
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve({ success: true });
                } else {
                  resolve({ success: false, error: xhr.responseText || `HTTP ${xhr.status}` });
                }
              };
              xhr.onerror = function() {
                resolve({ success: false, error: 'Network error' });
              };
              xhr.send(JSON.stringify(jewelryData));
            });
          };
          
          for (const row of jsonData) {
            try {
              // Check for duplicates first
              const dupeCheck = isDuplicate(row);
              if (dupeCheck.isDupe) {
                skippedCount++;
                errorMessages.push(`SKIPPED: ${dupeCheck.reason}`);
                continue;
              }
              
              // Get the appraised value - check multiple possible column names
              const appraisedValue = parseFloat(row['Appraised Value']) || parseFloat(row['Value']) || parseFloat(row['Purchase Price']) || 0;
              const purchasePrice = parseFloat(row['Purchase Price']) || parseFloat(row['Cost']) || appraisedValue || 0;
              
              // Convert originalPackaging string to boolean
              const origPkgValue = toStr(row['Original Packaging'] || '');
              const originalPackaging = origPkgValue.toLowerCase() === 'yes' || origPkgValue.toLowerCase() === 'true' || origPkgValue === '1';
              
              // Map Excel columns to API fields
              // Note: 'shares' must be 1 for jewelry (it's quantity), currentPrice holds the value
              const jewelryData = {
                symbol: toStr(row['Item Name'] || row['Name'] || row['Symbol'] || 'Unknown Item'),
                name: toStr(row['Model'] || row['Item Name'] || row['Name'] || ''),
                jewelryType: toStr(row['Type'] || row['Jewelry Type'] || 'Other'),
                brand: toStr(row['Brand'] || ''),
                model: toStr(row['Model'] || ''),
                material: toStr(row['Material'] || ''),
                serialNumber: toStr(row['Serial Number'] || ''),
                condition: toStr(row['Condition'] || ''),
                originalPackaging: origPkgValue ? originalPackaging : null,
                platform: toStr(row['Storage Location'] || row['Location'] || 'Unknown'),
                insuredBy: toStr(row['Insured By'] || ''),
                costBasis: purchasePrice,
                shares: 1, // Jewelry quantity is always 1
                currentPrice: appraisedValue,
                purchaseDate: toStr(row['Purchase Date'] || ''),
                purchaseLocation: toStr(row['Where Purchased'] || row['Purchased From'] || ''),
                purchaseCondition: toStr(row['Purchase Condition'] || ''),
                notes: toStr(row['Notes'] || ''),
                investmentType: 'JEWELRY'
              };
              
              // Call API to add the jewelry item
              const result = await addJewelryItem(jewelryData);
              
              if (result.success) {
                successCount++;
              } else {
                errorCount++;
                errorMessages.push(`${row['Item Name'] || 'Item'}: ${(result.error || 'Unknown error').substring(0, 100)}`);
                console.error('Failed to import:', row['Item Name'], result.error);
              }
            } catch (err) {
              errorCount++;
              errorMessages.push(`${row['Item Name'] || 'Item'}: ${err.message}`);
              console.error('Error importing row:', err);
            }
          }
          
          // Show result with error details if any
          let resultMessage = `Import complete!\n\nSuccessfully imported: ${successCount}\nSkipped (duplicates): ${skippedCount}\nFailed: ${errorCount}`;
          if (errorMessages.length > 0) {
            resultMessage += `\n\nDetails:\n${errorMessages.slice(0, 5).join('\n')}`;
            if (errorMessages.length > 5) {
              resultMessage += `\n... and ${errorMessages.length - 5} more`;
            }
          }
          alert(resultMessage);
          
          // Refresh data if any items were imported
          if (successCount > 0 && onRefresh) {
            onRefresh();
          }
        } catch (err) {
          console.error('Error parsing Excel file:', err);
          alert('Error reading the Excel file. Please make sure it\'s a valid Excel file.');
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Error importing file:', err);
      alert('Error importing file. Please try again.');
    }
  };

  // Toggle tab - clicking same tab hides it, clicking different tab shows it
  const handleTabClick = (tabType) => {
    if (activeTab === tabType) {
      setActiveTab(null); // Hide if clicking same tab
    } else {
      setActiveTab(tabType); // Show clicked tab
    }
  };

  // Filter to only non-market assets (alphabetized by display label)
  const nonMarketTypes = ['CASH', 'ELECTRONICS', 'FIREARMS', 'JEWELRY', 'PRIVATE INVESTMENTS', 'REAL ESTATE', 'VEHICLES'];
  const nonMarketAssets = useMemo(() => 
    stocks.filter(s => nonMarketTypes.includes(s.investmentType)),
    [stocks]
  );
  
  // Get all vehicles for the modal - sorted to match UI display order (memoized for performance)
  const allVehicles = useMemo(() => {
    return stocks.filter(s => s.investmentType === 'VEHICLES').sort((a, b) => {
      let aVal, bVal;
      switch (vehicleSortBy) {
        case 'year':
          aVal = a.vehicleYear || 0;
          bVal = b.vehicleYear || 0;
          break;
        case 'make':
          aVal = (a.vehicleMake || '').toLowerCase();
          bVal = (b.vehicleMake || '').toLowerCase();
          break;
        case 'value':
          aVal = a.currentPrice || a.shares || 0;
          bVal = b.currentPrice || b.shares || 0;
          break;
        case 'cost':
          aVal = a.costBasis || 0;
          bVal = b.costBasis || 0;
          break;
        case 'platform':
          aVal = (a.platform || '').toLowerCase();
          bVal = (b.platform || '').toLowerCase();
          break;
        default:
          aVal = (a.name || a.symbol || '').toLowerCase();
          bVal = (b.name || b.symbol || '').toLowerCase();
      }
      if (typeof aVal === 'string') {
        return vehicleSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return vehicleSortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [stocks, vehicleSortBy, vehicleSortDir]);
  
  // Calculate totals - use correct value logic (currentPrice if available, otherwise shares if > 1)
  const getActualValue = (a) => {
    const cp = a.currentPrice || 0;
    const sh = a.shares || 0;
    if (cp > 0) return cp;
    if (sh > 1) return sh; // Legacy: value stored in shares
    return cp;
  };
  const activeNonMarketAssets = nonMarketAssets.filter(a => !a.disposed);
  const totalValue = activeNonMarketAssets.reduce((sum, a) => sum + getActualValue(a), 0);
  const totalCost = activeNonMarketAssets.reduce((sum, a) => sum + (parseFloat(a.costBasis) || 0), 0);
  const totalGainLoss = totalValue - totalCost;
  const assetCount = nonMarketAssets.length;

  const handleAdd = (assetType) => {
    // For jewelry, open the detail modal instead of inline form
    if (assetType === 'JEWELRY') {
      setIsAddingNewJewelry(true);
      return;
    }
    // For firearms, open the detail modal instead of inline form
    if (assetType === 'FIREARMS') {
      setIsAddingNewGun(true);
      return;
    }
    // For electronics, open the detail modal instead of inline form
    if (assetType === 'ELECTRONICS') {
      setIsAddingNewElectronics(true);
      return;
    }
    // For art, open the detail modal instead of inline form
    if (assetType === 'ART') {
      setIsAddingNewArt(true);
      return;
    }
    // For vehicles, open the detail modal instead of inline form
    if (assetType === 'VEHICLES') {
      setIsAddingNewVehicle(true);
      return;
    }
    // For real estate, open the detail modal instead of inline form
    if (assetType === 'REAL ESTATE') {
      setIsAddingNewRealEstate(true);
      return;
    }
    // For private investments, open the detail modal instead of inline form
    if (assetType === 'PRIVATE INVESTMENTS') {
      setIsAddingNewPrivateInvestment(true);
      return;
    }
    setAddingType(assetType);
  };

  const handleAddSave = async (formData) => {
    if (onAddStock) {
      // For vehicles, construct the symbol from year/make/model
      let symbol = formData.symbol || 'ASSET';
      let name = formData.name || formData.symbol || 'Asset';
      
      if (formData.investmentType === 'VEHICLES') {
        if (formData.vehicleYear && formData.vehicleMake && formData.vehicleModel) {
          symbol = `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}`;
          name = symbol;
        } else {
          symbol = 'VEHICLE';
          name = 'Vehicle';
        }
      }
      
      // The "shares" field in the form is the balance/value
      const enteredValue = parseFloat(formData.shares) || 0;
      
      // For ALL non-market assets: shares=1, currentPrice=value
      // This is critical - the main portfolio calculation does shares * currentPrice
      await onAddStock({
        symbol: symbol,
        name: name,
        shares: 1,  // Always 1 for non-market assets
        costBasis: formData.costBasis || 0,
        platform: formData.platform || 'Unknown',
        investmentType: formData.investmentType,
        currentPrice: enteredValue,  // The actual value
        // Store cash-specific fields
        accountType: formData.accountType,
        accountNumber: formData.accountNumber,
        // Store vehicle-specific fields
        vehicleYear: formData.vehicleYear,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        vin: formData.vin,
        licensePlate: formData.licensePlate,
        insuredBy: formData.insuredBy,
        sirius: formData.sirius,
        mileage: formData.mileage,
        mileageDate: formData.mileageDate,
        notes: formData.notes,
      });
    }
    setAddingType(null);
  };

  const handleEdit = (asset) => {
    setEditingId(asset.id);
    
    // Determine the correct value to show in the Balance/Value field
    // Legacy data check: if shares > 1, use shares; otherwise use currentPrice
    let balanceValue;
    if (asset.shares && asset.shares > 1) {
      balanceValue = asset.shares;
    } else {
      balanceValue = asset.currentPrice || 0;
    }
    
    setEditForm({
      symbol: asset.symbol,
      name: asset.name,
      investmentType: asset.investmentType,
      // For non-market assets, show the calculated value in the shares field
      shares: balanceValue,
      costBasis: asset.costBasis,
      platform: asset.platform,
      // Include cash-specific fields
      accountType: asset.accountType,
      accountNumber: asset.accountNumber,
      // Include vehicle-specific fields
      vehicleYear: asset.vehicleYear,
      vehicleMake: asset.vehicleMake,
      vehicleModel: asset.vehicleModel,
      vin: asset.vin,
      licensePlate: asset.licensePlate,
      insuredBy: asset.insuredBy,
      sirius: asset.sirius,
      mileage: asset.mileage,
      mileageDate: asset.mileageDate,
      notes: asset.notes,
      // Include jewelry-specific fields
      jewelryType: asset.jewelryType,
      brand: asset.brand,
      model: asset.model,
      serialNumber: asset.serialNumber,
      condition: asset.condition,
      originalPackaging: asset.originalPackaging,
      purchaseYear: asset.purchaseYear,
      purchaseLocation: asset.purchaseLocation,
      purchaseDate: asset.purchaseDate,
      purchaseCondition: asset.purchaseCondition,
    });
  };

  const handleEditSave = async () => {
    if (onEditStock && editingId) {
      // For vehicles, reconstruct the symbol from year/make/model
      let updatedForm = { ...editForm };
      
      if (editForm.vehicleYear && editForm.vehicleMake && editForm.vehicleModel) {
        updatedForm.symbol = `${editForm.vehicleYear} ${editForm.vehicleMake} ${editForm.vehicleModel}`;
        updatedForm.name = updatedForm.symbol;
      }
      
      // The "shares" field in the form contains the value/balance
      const enteredValue = parseFloat(editForm.shares) || 0;
      
      // For ALL non-market assets: shares=1, currentPrice=value
      // This is critical - the main portfolio calculation does shares * currentPrice
      // So we must keep shares=1 and store the actual value in currentPrice
      await onEditStock(editingId, {
        ...updatedForm,
        shares: 1,  // Always 1 for non-market assets
        currentPrice: enteredValue,  // The actual value
      });
    }
    setEditingId(null);
    setEditForm({});
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = async (id) => {
    if (onDeleteStock && window.confirm('Are you sure you want to delete this asset?')) {
      await onDeleteStock(id);
    }
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 p-0 sm:p-4 bg-slate-900/95 backdrop-blur-sm overflow-auto' : ''}`}>
      <Card className={`overflow-hidden border-0 shadow-lg ${isFullscreen ? 'h-full flex flex-col rounded-none sm:rounded-xl sm:shadow-md sm:border-2 sm:border-slate-200' : ''} ${mobilePreview ? '' : ''}`}>
        {/* Mobile Header */}
        <div 
          className="sm:hidden bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-3 cursor-pointer"
          onClick={() => {
            if (isFullscreen) {
              onToggleFullscreen && onToggleFullscreen(false);
            } else {
              handleToggleExpanded();
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Non-Market Assets</h2>
                <p className="text-[10px] text-white/70">{privacyMode ? '••• holdings' : `${assetCount} holdings`}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Hide Values Toggle Button - always show */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHideNonMarketValues();
                }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                  hideNonMarketValues
                    ? 'bg-white/40 text-white' 
                    : 'bg-white/20 text-white/70 hover:bg-white/30'
                }`}
                title={hideNonMarketValues ? 'Show values' : 'Hide values'}
              >
                {hideNonMarketValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              {onToggleFullscreen && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFullscreen(!isFullscreen);
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white"
                >
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              )}
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        <CardHeader 
          className="hidden sm:block bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-pointer py-4"
          onClick={() => {
            if (isFullscreen) {
              onToggleFullscreen && onToggleFullscreen(false);
            } else {
              handleToggleExpanded();
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!isFullscreen && (
                <PanelMoveButtons
                  panelId="non-market-assets"
                  movePanelUp={movePanelUp}
                  movePanelDown={movePanelDown}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              )}
              <div className="bg-white/20 p-2 rounded-lg">
                <PiggyBank className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className={`font-bold ${isFullscreen ? 'text-base' : 'text-lg'}`}>Non-Market Assets</CardTitle>
                <p className="text-white/80 text-sm">Private holdings & tangible assets{privacyMode ? '' : ` • ${assetCount} holdings`}</p>
              </div>
              {/* Totals Summary - Show when values not hidden */}
              {!hideNonMarketValues && !privacyMode && assetCount > 0 && (
                <div className="hidden sm:flex items-center gap-4 ml-4 text-sm">
                  <span className="text-white/70">Cost: <span className="font-semibold text-white">${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  <span className="text-white/50">•</span>
                  <span className="text-white/70">Value: <span className="font-bold text-white">${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  <span className="text-white/50">•</span>
                  <span className={totalGainLoss >= 0 ? 'text-emerald-300' : 'text-red-300'}>
                    {totalGainLoss >= 0 ? 'Gain' : 'Loss'}: <span className="font-semibold">{totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Hide Values Toggle Button - always show so user can toggle local hide */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHideNonMarketValues();
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                  hideNonMarketValues
                    ? 'bg-white/40 text-white' 
                    : 'bg-white/20 text-white/70 hover:bg-white/30'
                }`}
                title={hideNonMarketValues ? 'Show values' : 'Hide values'}
              >
                {hideNonMarketValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="text-sm font-medium">{hideNonMarketValues ? 'Show $' : 'Hide $'}</span>
              </button>
              {/* Fullscreen Toggle Button */}
              {onToggleFullscreen && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFullscreen(!isFullscreen);
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors"
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
              )}
              
              {/* Collapse indicator - Hide in fullscreen mode */}
              {!isFullscreen && (
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Content */}
        {(isExpanded || isFullscreen) && (
          <CardContent className={`bg-white ${isFullscreen ? 'flex-1 overflow-auto p-4 sm:p-6' : 'p-4'}`}>
            {/* Category Toggle Buttons - Alphabetized by label */}
            <div className={`grid grid-cols-4 sm:grid-cols-8 gap-2 mb-4 ${isFullscreen ? 'sticky top-0 bg-white z-10 pb-2' : ''}`}>
              {Object.entries(ASSET_CONFIGS)
                .sort((a, b) => a[1].label.localeCompare(b[1].label))
                .map(([type, config]) => {
                const Icon = config.icon;
                const count = stocks.filter(s => s.investmentType === type).length;
                const isActive = activeTab === type;
                return (
                  <button 
                    key={type}
                    onClick={() => handleTabClick(type)}
                    data-testid={`nonmarket-tab-${type.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all border-2 ${
                      isActive 
                        ? 'bg-amber-50 text-amber-900 border-amber-400 shadow-md' 
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-transparent hover:border-slate-200'
                    }`}
                  >
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className={`text-[9px] sm:text-[10px] font-medium text-center leading-tight ${isActive ? 'text-amber-800' : 'text-slate-500'}`}>{config.label}</span>
                    {count > 0 && !hideNonMarketValues && (
                      <span className={`px-1 sm:px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold ${
                        isActive ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Show content only when a tab is selected */}
            {activeTab ? (
              <div className="mt-4">
                {addingType === activeTab && (
                  <AddAssetForm
                    assetType={activeTab}
                    onSave={handleAddSave}
                    onCancel={() => setAddingType(null)}
                  />
                )}
                <AssetTabContent
                  assetType={activeTab}
                  assets={stocks}
                  formatCurrency={formatCurrencyPrivate}
                  onAdd={handleAdd}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isViewer={isViewer}
                  editingId={editingId}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onEditSave={handleEditSave}
                  onEditCancel={handleEditCancel}
                  API_URL={API_URL}
                  userEmail={userEmail}
                  hideValues={hideNonMarketValues}
                  onVehicleDetailClick={activeTab === 'VEHICLES' ? setVehicleDetailModalId : null}
                  onRealEstateDetailClick={activeTab === 'REAL ESTATE' ? setRealEstateDetailModalId : null}
                  onJewelryDetailClick={activeTab === 'JEWELRY' ? (id, filteredList) => {
                    setJewelryDetailModalId(id);
                    if (filteredList) setFilteredJewelryForModal(filteredList);
                  } : null}
                  onGunDetailClick={activeTab === 'FIREARMS' ? (id, filteredList) => {
                    setGunDetailModalId(id);
                    if (filteredList) setFilteredGunsForModal(filteredList);
                  } : null}
                  onElectronicsDetailClick={activeTab === 'ELECTRONICS' ? (id, filteredList) => {
                    setElectronicsDetailModalId(id);
                    if (filteredList) setFilteredElectronicsForModal(filteredList);
                  } : null}
                  onArtDetailClick={activeTab === 'ART' ? (id, filteredList) => {
                    setArtDetailModalId(id);
                    if (filteredList) setFilteredArtForModal(filteredList);
                  } : null}
                  onPrivateInvestmentDetailClick={activeTab === 'PRIVATE INVESTMENTS' ? (id, filteredList) => {
                    setPrivateInvestmentDetailModalId(id);
                    if (filteredList) setFilteredPrivateInvestmentsForModal(filteredList);
                  } : null}
                  externalSortBy={activeTab === 'VEHICLES' ? vehicleSortBy : undefined}
                  externalSortDir={activeTab === 'VEHICLES' ? vehicleSortDir : undefined}
                  onSortChange={activeTab === 'VEHICLES' ? (newBy, newDir) => {
                    setVehicleSortBy(newBy);
                    setVehicleSortDir(newDir);
                  } : undefined}
                  jewelryViewMode={activeTab === 'JEWELRY' ? jewelryViewMode : undefined}
                  onToggleJewelryView={activeTab === 'JEWELRY' ? toggleJewelryViewMode : undefined}
                  jewelryGallerySize={activeTab === 'JEWELRY' ? jewelryGallerySize : undefined}
                  onToggleJewelrySize={activeTab === 'JEWELRY' ? toggleJewelryGallerySize : undefined}
                  vehicleViewMode={activeTab === 'VEHICLES' ? vehicleViewMode : undefined}
                  onToggleVehicleView={activeTab === 'VEHICLES' ? toggleVehicleViewMode : undefined}
                  vehicleGallerySize={activeTab === 'VEHICLES' ? vehicleGallerySize : undefined}
                  onToggleVehicleSize={activeTab === 'VEHICLES' ? toggleVehicleGallerySize : undefined}
                  realEstateViewMode={activeTab === 'REAL ESTATE' ? realEstateViewMode : undefined}
                  onToggleRealEstateView={activeTab === 'REAL ESTATE' ? toggleRealEstateViewMode : undefined}
                  realEstateGallerySize={activeTab === 'REAL ESTATE' ? realEstateGallerySize : undefined}
                  onToggleRealEstateSize={activeTab === 'REAL ESTATE' ? toggleRealEstateGallerySize : undefined}
                  firearmsViewMode={activeTab === 'FIREARMS' ? firearmsViewMode : undefined}
                  onToggleFirearmsView={activeTab === 'FIREARMS' ? toggleFirearmsViewMode : undefined}
                  firearmsGallerySize={activeTab === 'FIREARMS' ? firearmsGallerySize : undefined}
                  onToggleFirearmsSize={activeTab === 'FIREARMS' ? toggleFirearmsGallerySize : undefined}
                  electronicsViewMode={activeTab === 'ELECTRONICS' ? electronicsViewMode : undefined}
                  onToggleElectronicsView={activeTab === 'ELECTRONICS' ? toggleElectronicsViewMode : undefined}
                  electronicsGallerySize={activeTab === 'ELECTRONICS' ? electronicsGallerySize : undefined}
                  onToggleElectronicsSize={activeTab === 'ELECTRONICS' ? toggleElectronicsGallerySize : undefined}
                  artViewMode={activeTab === 'ART' ? artViewMode : undefined}
                  onToggleArtView={activeTab === 'ART' ? toggleArtViewMode : undefined}
                  artGallerySize={activeTab === 'ART' ? artGallerySize : undefined}
                  onToggleArtSize={activeTab === 'ART' ? toggleArtGallerySize : undefined}
                  onJewelryExport={activeTab === 'JEWELRY' ? handleJewelryExport : undefined}
                  onJewelryImport={activeTab === 'JEWELRY' ? handleJewelryImport : undefined}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <PiggyBank className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Click a category above to view assets</p>
              </div>
            )}
          </CardContent>
        )}
      
      {/* Vehicle Detail Modal - Rendered at panel level for stability */}
      {vehicleDetailModalId && (() => {
        // These lookups are minimal and fast - the memoized allVehicles helps here
        const currentVehicle = allVehicles.find(v => v.id === vehicleDetailModalId);
        if (!currentVehicle) return null;
        
        const currentIndex = allVehicles.indexOf(currentVehicle);
        
        return (
          <VehicleDetailModal
            key={vehicleDetailModalId}
            vehicle={currentVehicle}
            vehicles={allVehicles}
            currentIndex={currentIndex}
            onClose={() => setVehicleDetailModalId(null)}
            onSave={async (updatedVehicle, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${updatedVehicle.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updatedVehicle,
                    userEmail
                  })
                });
                if (response.ok) {
                  // Always refresh data after successful save
                  if (onRefresh) await onRefresh();
                  // Only close modal if shouldClose is true
                  if (shouldClose) {
                    setVehicleDetailModalId(null);
                  }
                  // Stay on Vehicles tab
                  setActiveTab('VEHICLES');
                } else {
                  const error = await response.json().catch(() => ({}));
                  console.error('Error saving vehicle:', error);
                  alert('Failed to save: ' + (error.detail || 'Unknown error'));
                }
              } catch (error) {
                console.error('Error saving vehicle:', error);
                alert('Failed to save vehicle');
              }
            }}
            onNavigate={(newIndex) => {
              const newVehicle = allVehicles[newIndex];
              if (newVehicle) {
                setVehicleDetailModalId(newVehicle.id);
              }
            }}
            formatCurrency={(amount) => {
              if (hideNonMarketValues) return '•••••';
              return '$' + parseFloat(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}

      {/* Real Estate Detail Modal - Rendered at panel level for stability */}
      {realEstateDetailModalId && (() => {
        const allProperties = stocks.filter(s => s.investmentType === 'REAL ESTATE');
        const currentProperty = allProperties.find(p => p.id === realEstateDetailModalId);
        if (!currentProperty) return null;
        
        const currentIndex = allProperties.indexOf(currentProperty);
        
        return (
          <RealEstateDetailModal
            key={realEstateDetailModalId}
            property={currentProperty}
            properties={allProperties}
            currentIndex={currentIndex}
            onClose={() => setRealEstateDetailModalId(null)}
            onSave={async (propertyId, updatedProperty, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${propertyId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updatedProperty,
                    userEmail
                  })
                });
                if (response.ok) {
                  if (onRefresh) {
                    await onRefresh();
                  }
                  if (shouldClose) {
                    setRealEstateDetailModalId(null);
                  }
                  setActiveTab('REAL ESTATE');
                } else {
                  const error = await response.json();
                  console.error('Error saving property:', error);
                  alert('Failed to save: ' + (error.detail || 'Unknown error'));
                }
              } catch (error) {
                console.error('Error saving property:', error);
                alert('Failed to save property');
              }
            }}
            onNavigate={(direction) => {
              const allProperties = stocks.filter(s => s.investmentType === 'REAL ESTATE');
              const currentIndex = allProperties.findIndex(p => p.id === realEstateDetailModalId);
              let newIndex;
              if (direction === 'prev') {
                newIndex = currentIndex - 1;
              } else {
                newIndex = currentIndex + 1;
              }
              if (newIndex >= 0 && newIndex < allProperties.length) {
                setRealEstateDetailModalId(allProperties[newIndex].id);
              }
            }}
            formatCurrency={(amount) => {
              if (hideNonMarketValues) return '•••••';
              return '$' + parseFloat(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Jewelry Detail Modal - Rendered at panel level for stability */}
      {jewelryDetailModalId && (() => {
        // Use filtered list if available, otherwise use all jewelry
        const jewelryList = filteredJewelryForModal.length > 0 
          ? filteredJewelryForModal 
          : stocks.filter(s => s.investmentType === 'JEWELRY');
        const currentItem = jewelryList.find(j => j.id === jewelryDetailModalId);
        if (!currentItem) return null;
        
        const currentIndex = jewelryList.indexOf(currentItem);
        
        return (
          <JewelryDetailModal
            key={jewelryDetailModalId}
            item={currentItem}
            items={jewelryList}
            currentIndex={currentIndex}
            onClose={() => {
              setJewelryDetailModalId(null);
              setFilteredJewelryForModal([]); // Clear filtered list when closing
            }}
            onSave={async (itemId, updatedItem, shouldClose = true) => {
              console.log('Jewelry onSave called with itemId:', itemId, 'shouldClose:', shouldClose);
              try {
                console.log('Sending PUT request to update jewelry item...');
                const response = await fetch(`${API_URL}/api/portfolio/${itemId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updatedItem,
                    userEmail
                  })
                });
                
                console.log('PUT response status:', response.status);
                
                if (response.ok) {
                  // Refresh data FIRST to update the list
                  if (onRefresh) {
                    console.log('Refreshing data after jewelry edit...');
                    await onRefresh();
                    console.log('Refresh completed');
                  }
                  setActiveTab('JEWELRY');
                  // Close modal after data is refreshed
                  if (shouldClose) {
                    console.log('Closing jewelry modal...');
                    setJewelryDetailModalId(null);
                    setFilteredJewelryForModal([]); // Clear filtered list when closing
                  }
                } else {
                  // Try to get error details, but handle if body already used
                  let errorMessage = 'Unknown error';
                  try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || 'Unknown error';
                  } catch (jsonError) {
                    errorMessage = `HTTP ${response.status} error`;
                  }
                  console.error('Error saving jewelry item:', errorMessage);
                  alert('Failed to save: ' + errorMessage);
                }
              } catch (error) {
                console.error('Error saving jewelry item:', error);
                alert('Failed to save item: ' + error.message);
              }
            }}
            onNavigate={(direction) => {
              // Use filtered list for navigation to stay within filtered subset
              const jewelryList = filteredJewelryForModal.length > 0 
                ? filteredJewelryForModal 
                : stocks.filter(s => s.investmentType === 'JEWELRY');
              const currentIndex = jewelryList.findIndex(j => j.id === jewelryDetailModalId);
              let newIndex;
              if (direction === 'prev') {
                newIndex = currentIndex - 1;
              } else {
                newIndex = currentIndex + 1;
              }
              if (newIndex >= 0 && newIndex < jewelryList.length) {
                setJewelryDetailModalId(jewelryList[newIndex].id);
              }
            }}
            formatCurrency={formatCurrencyPrivate}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Add New Jewelry Modal */}
      {isAddingNewJewelry && (
        <JewelryDetailModal
          key="new-jewelry"
          item={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'JEWELRY',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            jewelryType: '',
            brand: '',
            model: '',
            serialNumber: '',
            condition: '',
            originalPackaging: '',
            insuredBy: '',
            purchaseDate: '',
            purchaseLocation: '',
            purchaseCondition: '',
            notes: '',
            photos: [],
            documents: []
          }}
          items={[]}
          currentIndex={0}
          onClose={() => {
            setIsAddingNewJewelry(false);
          }}
          onSave={async (itemId, newItem, shouldClose = true) => {
            // Prevent double submission
            if (window._savingJewelry) return;
            window._savingJewelry = true;
            
            console.log('Adding jewelry - newItem:', newItem);
            
            try {
              // currentPrice contains the actual appraised value from the form
              // shares is always 1 for non-market assets, so check currentPrice first
              const enteredValue = parseFloat(newItem.currentPrice) || 0;
              
              // Build payload, omitting null/undefined values
              const payload = {
                symbol: newItem.symbol || 'JEWELRY ITEM',
                name: newItem.symbol || 'Jewelry Item',
                shares: 1,
                costBasis: parseFloat(newItem.costBasis) || 0,
                currentPrice: enteredValue || 1,
                platform: newItem.platform || '',
                investmentType: 'JEWELRY'
              };
              
              // Only add optional fields if they have values
              if (newItem.jewelryType) payload.jewelryType = newItem.jewelryType;
              if (newItem.brand) payload.brand = newItem.brand;
              if (newItem.model) payload.model = newItem.model;
              if (newItem.material) payload.material = newItem.material;
              if (newItem.serialNumber) payload.serialNumber = newItem.serialNumber;
              if (newItem.condition) payload.condition = newItem.condition;
              if (newItem.originalPackaging) payload.originalPackaging = newItem.originalPackaging;
              if (newItem.insuredBy) payload.insuredBy = newItem.insuredBy;
              if (newItem.purchaseDate) payload.purchaseDate = newItem.purchaseDate;
              if (newItem.purchaseLocation) payload.purchaseLocation = newItem.purchaseLocation;
              if (newItem.purchaseCondition) payload.purchaseCondition = newItem.purchaseCondition;
              if (newItem.purchaseYear) payload.purchaseYear = parseInt(newItem.purchaseYear);
              if (newItem.notes) payload.notes = newItem.notes;
              
              // Always include photos and documents arrays (even if empty for debugging)
              console.log('Photos from newItem:', newItem.photos);
              console.log('Documents from newItem:', newItem.documents);
              if (newItem.photos && newItem.photos.length > 0) {
                payload.photos = newItem.photos;
                console.log('Added photos to payload:', payload.photos);
              }
              if (newItem.documents && newItem.documents.length > 0) {
                payload.documents = newItem.documents;
                console.log('Added documents to payload:', payload.documents);
              }
              
              console.log('Sending payload:', JSON.stringify(payload));
              
              // Use XMLHttpRequest to avoid fetch response body issues
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, true);
              xhr.setRequestHeader('Content-Type', 'application/json');
              
              xhr.onload = async function() {
                console.log('XHR status:', xhr.status, 'Response:', xhr.responseText);
                if (xhr.status >= 200 && xhr.status < 300) {
                  // Refresh data FIRST, then close modal
                  if (onRefresh) {
                    console.log('Calling onRefresh to update list...');
                    await onRefresh();
                    console.log('onRefresh completed');
                  }
                  setActiveTab('JEWELRY');
                  if (shouldClose) setIsAddingNewJewelry(false);
                } else {
                  alert('Failed to add jewelry. Status: ' + xhr.status + '\n' + xhr.responseText);
                }
                window._savingJewelry = false;
              };
              
              xhr.onerror = function() {
                console.error('XHR error');
                alert('Failed to add jewelry - network error');
                window._savingJewelry = false;
              };
              
              xhr.send(JSON.stringify(payload));
            } catch (err) {
              console.error('Error adding jewelry:', err);
              alert('Failed to add: ' + err.message);
              window._savingJewelry = false;
            }
          }}
          onNavigate={() => {}}
          formatCurrency={formatCurrencyPrivate}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Gun/Firearm Detail Modal */}
      {gunDetailModalId && (() => {
        const allGuns = stocks.filter(s => s.investmentType === 'FIREARMS');
        const gunsList = filteredGunsForModal.length > 0 ? filteredGunsForModal : allGuns;
        const currentGun = gunsList.find(g => g.id === gunDetailModalId);
        if (!currentGun) return null;
        const currentIndex = gunsList.indexOf(currentGun);
        
        return (
          <GunDetailModal
            key={gunDetailModalId}
            item={currentGun}
            items={gunsList}
            currentIndex={currentIndex}
            onClose={() => {
              setGunDetailModalId(null);
              setFilteredGunsForModal([]);
            }}
            onSave={async (updatedGun, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${updatedGun.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updatedGun,
                    currentPrice: updatedGun.shares,
                    userEmail
                  })
                });
                if (response.ok) {
                  if (onRefresh) await onRefresh();
                  if (shouldClose) {
                    setGunDetailModalId(null);
                    setFilteredGunsForModal([]);
                  }
                } else {
                  alert('Failed to save firearm');
                }
              } catch (err) {
                console.error('Error saving firearm:', err);
                alert('Failed to save firearm');
              }
            }}
            onNavigate={(direction) => {
              const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
              if (newIndex >= 0 && newIndex < gunsList.length) {
                setGunDetailModalId(gunsList[newIndex].id);
              }
            }}
            formatCurrency={formatCurrencyPrivate}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Add New Gun Modal */}
      {isAddingNewGun && (
        <GunDetailModal
          key="new-gun"
          item={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'FIREARMS',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            gunManufacturer: '',
            gunModel: '',
            gunType: '',
            gunAction: '',
            gunCaliber: '',
            gunCountry: '',
            serialNumber: '',
            condition: '',
            purchaseDate: '',
            purchaseLocation: '',
            appraisedValue: 0,
            lastValuationDate: '',
            isInsured: '',
            insuredBy: '',
            notes: '',
            photos: [],
            documents: []
          }}
          items={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewGun(false)}
          onSave={async (newGun, shouldClose = true) => {
            if (window._savingGun) return;
            window._savingGun = true;
            
            try {
              const enteredValue = parseFloat(newGun.currentPrice) || parseFloat(newGun.shares) || 0;
              
              const payload = {
                symbol: newGun.symbol || 'FIREARM',
                name: newGun.symbol || 'Firearm',
                shares: 1,
                costBasis: parseFloat(newGun.costBasis) || 0,
                currentPrice: enteredValue || 1,
                platform: newGun.platform || '',
                investmentType: 'FIREARMS',
                gunManufacturer: newGun.gunManufacturer || '',
                gunModel: newGun.gunModel || '',
                gunType: newGun.gunType || '',
                gunAction: newGun.gunAction || '',
                gunCaliber: newGun.gunCaliber || '',
                gunCountry: newGun.gunCountry || '',
                serialNumber: newGun.serialNumber || '',
                condition: newGun.condition || '',
                purchaseDate: newGun.purchaseDate || '',
                purchaseLocation: newGun.purchaseLocation || '',
                storageLocation: newGun.storageLocation || '',
                appraisedValue: parseFloat(newGun.appraisedValue) || 0,
                lastValuationDate: newGun.lastValuationDate || '',
                isInsured: newGun.isInsured || '',
                insuredBy: newGun.insuredBy || '',
                notes: newGun.notes || '',
                photos: newGun.photos || [],
                documents: newGun.documents || []
              };
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                setActiveTab('FIREARMS');
                if (shouldClose) setIsAddingNewGun(false);
              } else {
                const err = await response.json().catch(() => ({}));
                alert('Failed to add firearm: ' + (err.detail || response.status));
              }
            } catch (err) {
              console.error('Error adding firearm:', err);
              alert('Failed to add: ' + err.message);
            } finally {
              window._savingGun = false;
            }
          }}
          onNavigate={() => {}}
          formatCurrency={formatCurrencyPrivate}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Electronics Detail Modal */}
      {electronicsDetailModalId && (() => {
        const electronicsList = stocks.filter(a => a.investmentType === 'ELECTRONICS' || a.assetType === 'ELECTRONICS');
        const listToUse = filteredElectronicsForModal.length > 0 ? filteredElectronicsForModal : electronicsList;
        const currentElectronics = electronicsList.find(e => e.id === electronicsDetailModalId);
        const currentElectronicsIndex = listToUse.findIndex(e => e.id === electronicsDetailModalId);
        if (!currentElectronics) return null;
        
        return (
          <ElectronicsDetailModal
            key={electronicsDetailModalId}
            item={currentElectronics}
            items={listToUse}
            currentIndex={currentElectronicsIndex}
            onClose={() => {
              setElectronicsDetailModalId(null);
              setFilteredElectronicsForModal([]);
            }}
            onSave={async (updatedItem, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${updatedItem.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...updatedItem, userEmail })
                });
                if (response.ok && onRefresh) {
                  await onRefresh();
                  if (shouldClose) {
                    setElectronicsDetailModalId(null);
                    setFilteredElectronicsForModal([]);
                  }
                }
              } catch (error) {
                console.error('Error saving electronics:', error);
              }
            }}
            onNavigate={(direction) => {
              const newIndex = currentElectronicsIndex + direction;
              if (newIndex >= 0 && newIndex < listToUse.length) {
                setElectronicsDetailModalId(listToUse[newIndex].id);
              }
            }}
            formatCurrency={formatCurrencyPrivate}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Add New Electronics Modal */}
      {isAddingNewElectronics && (
        <ElectronicsDetailModal
          key="new-electronics"
          item={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'ELECTRONICS',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            electronicsBrand: '',
            electronicsModel: '',
            electronicsType: '',
            serialNumber: '',
            condition: '',
            purchaseDate: '',
            purchaseLocation: '',
            insuredBy: '',
            notes: '',
            photos: [],
            documents: []
          }}
          items={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewElectronics(false)}
          onSave={async (newItem, shouldClose = true) => {
            try {
              const enteredValue = parseFloat(newItem.shares) || 0;
              
              const payload = {
                symbol: newItem.symbol || 'ELECTRONICS',
                name: newItem.symbol || 'Electronics',
                shares: 1,
                costBasis: parseFloat(newItem.costBasis) || 0,
                currentPrice: enteredValue || 1,
                platform: newItem.platform || '',
                investmentType: 'ELECTRONICS'
              };
              
              if (newItem.electronicsBrand) payload.electronicsBrand = newItem.electronicsBrand;
              if (newItem.electronicsModel) payload.electronicsModel = newItem.electronicsModel;
              if (newItem.electronicsType) payload.electronicsType = newItem.electronicsType;
              if (newItem.serialNumber) payload.serialNumber = newItem.serialNumber;
              if (newItem.condition) payload.condition = newItem.condition;
              if (newItem.purchaseDate) payload.purchaseDate = newItem.purchaseDate;
              if (newItem.purchaseLocation) payload.purchaseLocation = newItem.purchaseLocation;
              if (newItem.insuredBy) payload.insuredBy = newItem.insuredBy;
              if (newItem.notes) payload.notes = newItem.notes;
              if (newItem.photos && newItem.photos.length > 0) payload.photos = newItem.photos;
              if (newItem.documents && newItem.documents.length > 0) payload.documents = newItem.documents;
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                if (shouldClose) {
                  setIsAddingNewElectronics(false);
                }
              } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Server error: ${response.status}`);
              }
            } catch (err) {
              console.error('Error adding electronics:', err);
              alert('Failed to add: ' + err.message);
            }
          }}
          onNavigate={() => {}}
          formatCurrency={formatCurrencyPrivate}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Art Detail Modal */}
      {artDetailModalId && (() => {
        const allArt = stocks.filter(s => s.investmentType === 'ART');
        const artList = filteredArtForModal.length > 0 ? filteredArtForModal : allArt;
        const currentItem = artList.find(a => a.id === artDetailModalId);
        if (!currentItem) return null;
        const currentIndex = artList.indexOf(currentItem);
        
        return (
          <ArtDetailModal
            key={artDetailModalId}
            item={currentItem}
            items={artList}
            currentIndex={currentIndex}
            onClose={() => setArtDetailModalId(null)}
            onSave={async (itemId, updateData, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${itemId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updateData,
                    userEmail
                  })
                });
                if (response.ok) {
                  if (onRefresh) {
                    await onRefresh();
                  }
                  if (shouldClose) {
                    setArtDetailModalId(null);
                  }
                }
              } catch (err) {
                console.error('Error saving art:', err);
              }
            }}
            onNavigate={(direction) => {
              const artList = filteredArtForModal.length > 0 
                ? filteredArtForModal 
                : stocks.filter(s => s.investmentType === 'ART');
              const currentIndex = artList.findIndex(a => a.id === artDetailModalId);
              let newIndex;
              if (direction === 'prev') {
                newIndex = Math.max(0, currentIndex - 1);
              } else {
                newIndex = Math.min(artList.length - 1, currentIndex + 1);
              }
              if (artList[newIndex]) {
                setArtDetailModalId(artList[newIndex].id);
              }
            }}
            formatCurrency={formatCurrencyPrivate}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Add New Art Modal */}
      {isAddingNewArt && (
        <ArtDetailModal
          key="new-art"
          item={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'ART',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            artArtist: '',
            artMedium: '',
            artYear: '',
            artDimensions: '',
            serialNumber: '',
            condition: '',
            purchaseDate: '',
            purchaseLocation: '',
            insuredBy: '',
            notes: '',
            photos: [],
            documents: []
          }}
          items={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewArt(false)}
          onSave={async (id, newItem, shouldClose = true) => {
            try {
              const enteredValue = parseFloat(newItem.shares) || 0;
              
              const payload = {
                symbol: newItem.symbol || 'ART',
                name: newItem.symbol || 'Artwork',
                shares: 1,
                costBasis: parseFloat(newItem.costBasis) || 0,
                currentPrice: enteredValue || 1,
                platform: newItem.platform || '',
                investmentType: 'ART'
              };
              
              if (newItem.artArtist) payload.artArtist = newItem.artArtist;
              if (newItem.artMedium) payload.artMedium = newItem.artMedium;
              if (newItem.artYear) payload.artYear = newItem.artYear;
              if (newItem.artDimensions) payload.artDimensions = newItem.artDimensions;
              if (newItem.serialNumber) payload.serialNumber = newItem.serialNumber;
              if (newItem.condition) payload.condition = newItem.condition;
              if (newItem.purchaseDate) payload.purchaseDate = newItem.purchaseDate;
              if (newItem.purchaseLocation) payload.purchaseLocation = newItem.purchaseLocation;
              if (newItem.insuredBy) payload.insuredBy = newItem.insuredBy;
              if (newItem.notes) payload.notes = newItem.notes;
              if (newItem.photos && newItem.photos.length > 0) payload.photos = newItem.photos;
              if (newItem.documents && newItem.documents.length > 0) payload.documents = newItem.documents;
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                if (shouldClose) {
                  setIsAddingNewArt(false);
                }
              } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Server error: ${response.status}`);
              }
            } catch (err) {
              console.error('Error adding art:', err);
              alert('Failed to add: ' + err.message);
            }
          }}
          onNavigate={() => {}}
          formatCurrency={formatCurrencyPrivate}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Private Investment Detail Modal */}
      {privateInvestmentDetailModalId && (() => {
        const allInvestments = stocks.filter(s => s.investmentType === 'PRIVATE INVESTMENTS');
        const investmentsList = filteredPrivateInvestmentsForModal.length > 0 ? filteredPrivateInvestmentsForModal : allInvestments;
        const currentItem = investmentsList.find(a => a.id === privateInvestmentDetailModalId);
        if (!currentItem) return null;
        const currentIndex = investmentsList.indexOf(currentItem);
        
        return (
          <PrivateInvestmentDetailModal
            key={privateInvestmentDetailModalId}
            item={currentItem}
            items={investmentsList}
            currentIndex={currentIndex}
            onClose={() => {
              setPrivateInvestmentDetailModalId(null);
              setFilteredPrivateInvestmentsForModal([]);
            }}
            onSave={async (itemId, updateData, shouldClose = true) => {
              try {
                const response = await fetch(`${API_URL}/api/portfolio/${itemId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...updateData,
                    userEmail
                  })
                });
                if (response.ok) {
                  if (onRefresh) {
                    await onRefresh();
                  }
                  if (shouldClose) {
                    setPrivateInvestmentDetailModalId(null);
                    setFilteredPrivateInvestmentsForModal([]);
                  }
                }
              } catch (err) {
                console.error('Error saving private investment:', err);
              }
            }}
            onNavigate={(direction) => {
              const list = filteredPrivateInvestmentsForModal.length > 0 
                ? filteredPrivateInvestmentsForModal 
                : stocks.filter(s => s.investmentType === 'PRIVATE INVESTMENTS');
              const idx = list.findIndex(a => a.id === privateInvestmentDetailModalId);
              let newIndex;
              if (direction === 'prev') {
                newIndex = Math.max(0, idx - 1);
              } else {
                newIndex = Math.min(list.length - 1, idx + 1);
              }
              if (list[newIndex]) {
                setPrivateInvestmentDetailModalId(list[newIndex].id);
              }
            }}
            formatCurrency={formatCurrencyPrivate}
            API_URL={API_URL}
            userEmail={userEmail}
          />
        );
      })()}
      
      {/* Add New Private Investment Modal */}
      {isAddingNewPrivateInvestment && (
        <PrivateInvestmentDetailModal
          key="new-private-investment"
          item={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'PRIVATE INVESTMENTS',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            investmentStatus: '',
            contactPerson: '',
            interestRate: '',
            investmentDate: '',
            maturityDate: '',
            terms: '',
            notes: '',
            documents: []
          }}
          items={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewPrivateInvestment(false)}
          onSave={async (id, newItem, shouldClose = true) => {
            try {
              // Use currentPrice which is set by handleSave in the modal
              const enteredValue = parseFloat(newItem.currentPrice) || parseFloat(newItem.shares) || 0;
              
              const payload = {
                symbol: newItem.symbol || 'INVESTMENT',
                name: newItem.symbol || 'Investment',
                shares: 1,
                costBasis: parseFloat(newItem.costBasis) || 0,
                currentPrice: enteredValue,
                platform: newItem.platform || '',
                investmentType: 'PRIVATE INVESTMENTS'
              };
              
              if (newItem.investmentStatus) payload.investmentStatus = newItem.investmentStatus;
              if (newItem.contactPerson) payload.contactPerson = newItem.contactPerson;
              if (newItem.interestRate) payload.interestRate = newItem.interestRate;
              if (newItem.investmentDate) payload.investmentDate = newItem.investmentDate;
              if (newItem.maturityDate) payload.maturityDate = newItem.maturityDate;
              if (newItem.terms) payload.terms = newItem.terms;
              if (newItem.notes) payload.notes = newItem.notes;
              if (newItem.documents && newItem.documents.length > 0) payload.documents = newItem.documents;
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                if (shouldClose) {
                  setIsAddingNewPrivateInvestment(false);
                }
              } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Server error: ${response.status}`);
              }
            } catch (err) {
              console.error('Error adding private investment:', err);
              alert('Failed to add: ' + err.message);
            }
          }}
          onNavigate={() => {}}
          formatCurrency={formatCurrencyPrivate}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Add New Vehicle Modal */}
      {isAddingNewVehicle && (
        <VehicleDetailModal
          key="new-vehicle"
          vehicle={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'VEHICLES',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            vehicleYear: '',
            vehicleMake: '',
            vehicleModel: '',
            vin: '',
            licensePlate: '',
            mileage: '',
            mileageDate: '',
            sirius: '',
            platform: '',
            insuredBy: '',
            notes: '',
            photos: [],
            documents: [],
            maintenanceLog: []
          }}
          vehicles={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewVehicle(false)}
          onSave={async (updatedVehicle) => {
            // Prevent double submission
            if (isAddingNewVehicle === 'saving') return;
            setIsAddingNewVehicle('saving');
            
            try {
              // currentPrice is set by VehicleDetailModal from editForm.shares
              const enteredValue = parseFloat(updatedVehicle.currentPrice) || 0;
              const symbol = `${updatedVehicle.vehicleYear || ''} ${updatedVehicle.vehicleMake || ''} ${updatedVehicle.vehicleModel || ''}`.trim() || 'VEHICLE';
              
              const payload = {
                symbol: symbol,
                name: symbol,
                shares: 1,
                costBasis: parseFloat(updatedVehicle.costBasis) || 0,
                currentPrice: enteredValue || 1,
                investmentType: 'VEHICLES'
              };
              
              // Vehicle-specific fields - use exact field names from assetConfigs
              if (updatedVehicle.vehicleYear) payload.vehicleYear = updatedVehicle.vehicleYear;
              if (updatedVehicle.vehicleMake) payload.vehicleMake = updatedVehicle.vehicleMake;
              if (updatedVehicle.vehicleModel) payload.vehicleModel = updatedVehicle.vehicleModel;
              if (updatedVehicle.vin) payload.vin = updatedVehicle.vin;
              if (updatedVehicle.licensePlate) payload.licensePlate = updatedVehicle.licensePlate;
              if (updatedVehicle.mileage) payload.mileage = updatedVehicle.mileage;
              if (updatedVehicle.mileageDate) payload.mileageDate = updatedVehicle.mileageDate;
              if (updatedVehicle.sirius) payload.sirius = updatedVehicle.sirius;
              if (updatedVehicle.platform) payload.platform = updatedVehicle.platform;
              if (updatedVehicle.insuredBy) payload.insuredBy = updatedVehicle.insuredBy;
              if (updatedVehicle.notes) payload.notes = updatedVehicle.notes;
              if (updatedVehicle.photos && updatedVehicle.photos.length > 0) payload.photos = updatedVehicle.photos;
              if (updatedVehicle.documents && updatedVehicle.documents.length > 0) payload.documents = updatedVehicle.documents;
              if (updatedVehicle.maintenanceLog && updatedVehicle.maintenanceLog.length > 0) payload.maintenanceLog = updatedVehicle.maintenanceLog;
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                setIsAddingNewVehicle(false);
              } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Server error: ${response.status}`);
              }
            } catch (err) {
              console.error('Error adding vehicle:', err);
              alert('Failed to add: ' + err.message);
              setIsAddingNewVehicle(true); // Reset to allow retry
            }
          }}
          onNavigate={() => {}}
          formatCurrency={(amount) => {
            if (hideNonMarketValues) return '•••••';
            return '$' + parseFloat(amount || 0).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
      
      {/* Add New Real Estate Modal */}
      {isAddingNewRealEstate && (
        <RealEstateDetailModal
          key="new-realestate"
          property={{
            id: 'new',
            symbol: '',
            name: '',
            investmentType: 'REAL ESTATE',
            shares: 0,
            costBasis: 0,
            currentPrice: 0,
            platform: '',
            squareFeet: '',
            bedrooms: '',
            bathrooms: '',
            lotSize: '',
            purchaseYear: '',
            ownedBy: '',
            insuredBy: '',
            notes: '',
            photos: [],
            documents: []
          }}
          properties={[]}
          currentIndex={0}
          onClose={() => setIsAddingNewRealEstate(false)}
          onSave={async (itemId, updateData, shouldClose = true) => {
            // Prevent double submission
            if (isAddingNewRealEstate === 'saving') return;
            setIsAddingNewRealEstate('saving');
            
            try {
              // currentPrice is set by the modal from editForm.shares (the Current Value field)
              const enteredValue = parseFloat(updateData.currentPrice) || 0;
              const propertyName = updateData.symbol || updateData.name || 'PROPERTY';
              
              const payload = {
                symbol: propertyName,
                name: updateData.name || propertyName,
                shares: 1,
                costBasis: parseFloat(updateData.costBasis) || 0,
                currentPrice: enteredValue || 1,
                investmentType: 'REAL ESTATE',
                // Map modal field names to database field names
                platform: updateData.platform || '',
                propertyAddress: updateData.name || '',
                propertyType: updateData.platform || '',
                squareFeet: updateData.squareFeet || '',
                propertySquareFeet: updateData.squareFeet || '',
                bedrooms: updateData.bedrooms || '',
                propertyBedrooms: updateData.bedrooms || '',
                bathrooms: updateData.bathrooms || '',
                propertyBathrooms: updateData.bathrooms || '',
                lotSize: updateData.lotSize || '',
                propertyLotSize: updateData.lotSize || '',
                purchaseYear: updateData.purchaseYear || '',
                propertyYearPurchased: updateData.purchaseYear || '',
                ownedBy: updateData.ownedBy || '',
                insuredBy: updateData.insuredBy || '',
                notes: updateData.notes || ''
              };
              
              if (updateData.photos && updateData.photos.length > 0) payload.photos = updateData.photos;
              if (updateData.documents && updateData.documents.length > 0) payload.documents = updateData.documents;
              
              const response = await fetch(`${API_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              
              if (response.ok) {
                if (onRefresh) await onRefresh();
                setIsAddingNewRealEstate(false);
              } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Server error: ${response.status}`);
              }
            } catch (err) {
              console.error('Error adding real estate:', err);
              alert('Failed to add: ' + err.message);
              setIsAddingNewRealEstate(true); // Reset to allow retry
            }
          }}
          onNavigate={() => {}}
          formatCurrency={(amount) => {
            if (hideNonMarketValues) return '•••••';
            return '$' + parseFloat(amount || 0).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }}
          API_URL={API_URL}
          userEmail={userEmail}
          isNewItem={true}
        />
      )}
    </Card>
    </div>
  );
};

export default NonMarketAssetsPanel;
